import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Fade,
  Slide,
  Divider,
  Collapse,
  Drawer,
  SwipeableDrawer,
  Switch,
  FormControlLabel,
  Dialog,
  AppBar,
  Toolbar,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { MobileInlineResults } from "./MobileInlineResults";
import { MobileExamsFlow } from "../../laboratory/components/MobileExamsFlow";
import { DESIGN_TOKENS } from "../../../design-system/tokens/colors";
import {
  Send,
  SmartToy,
  Person,
  CheckCircle,
  Schedule,
  Error,
  ArrowBack,
  Psychology,
  Agriculture,
  Pets,
  LocalDrink,
  LocalHospital,
  Restaurant,
  Inventory,
  Security,
  Add,
  Science,
  Close,
  ExpandMore,
} from "@mui/icons-material";
import { useChatContext } from "../contexts/ChatContext";
import {
  ChatMessage,
  Organization,
  MessageViewProps,
} from "../types/chatTypes";
import { flowiseService } from "../services/FlowiseService";
import {
  getOrganizationColor,
  getOrganizationIcon,
  BRAND_COLORS,
  NEUTRAL_COLORS,
  SPACING,
  AVATAR_SIZES,
  BORDER_RADIUS,
  STATUS_COLORS,
  COMMON_STYLES,
} from "../styles/constants";
import { useDashboard } from "../../../contexts/DashboardContext";
import { useAuthContext } from "../../../contexts/AuthContext";
import { SpeechButton } from "../../../components/ui/SpeechButton";
import { useTranslation } from "react-i18next";
import { TransitionProps } from "@mui/material/transitions";

// Helper functions for flow management
const getFlowIcon = (flowId: string) => {
  switch (flowId) {
    case "master":
      return <Psychology sx={{ fontSize: 32, color: DESIGN_TOKENS.colors.brand.primary }} />;
    case "pastos":
      return (
        <img
          src="/gestores/Pastos.svg"
          alt="Pastos"
          style={{ width: 32, height: 32 }}
        />
      );
    case "lifecycle-born":
    case "animal":
      return (
        <img src="/vaca.svg" alt="Animal" style={{ width: 32, height: 32 }} />
      );
    case "ordenha":
    case "dairy":
      return (
        <img
          src="/gestores/Ordenha.svg"
          alt="Ordenha"
          style={{ width: 32, height: 32 }}
        />
      );
    case "health":
    case "sanidade":
      return (
        <img
          src="/gestores/seringa.svg"
          alt="Sanidade"
          style={{ width: 32, height: 32 }}
        />
      );
    case "nutrition":
    case "nutricao":
      return (
        <img
          src="/gestores/nutricao.svg"
          alt="Nutrição"
          style={{ width: 32, height: 32 }}
        />
      );
    case "batch":
    case "lotes":
      return (
        <img
          src="/gestores/lote.svg"
          alt="Lote"
          style={{ width: 32, height: 32 }}
        />
      );
    case "permissions":
    case "permissoes":
      return <Security sx={{ fontSize: 32, color: DESIGN_TOKENS.colors.brand.primary }} />;
    case "laboratory":
    case "gestor_laboratorial":
      return (
        <img
          src="/gestores/Laboratório.svg"
          alt="Laboratório"
          style={{ width: 32, height: 32 }}
        />
      );
    case "reproducao":
    case "gestor_reproducao":
      return (
        <img src="/gestores/reproducao.svg" alt="Reprodução" style={{ width: 32, height: 32 }} />
      );
    default:
      return <SmartToy sx={{ fontSize: 32, color: DESIGN_TOKENS.colors.brand.primary }} />;
  }
};

const LAB_VIEWS = new Set([
  "examsHub",
  "mastitisIntro",
  "mastitisTeatStep",
  "mastitisAnimalIdStep",
  "mastitisTestStep1",
  "mastitisTestStep2",
  "mastitisTestStep3",
  "exam-list",
  "exam-detail",
]);

const isLabView = (view?: string) => (view ? LAB_VIEWS.has(view) : false);

const MobileExamsTransition = React.forwardRef(function MobileExamsTransition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const getAvailableFlows = async (
  organizationType?: string,
  userPermissions?: any[],
  isOwner?: boolean,
  userId?: string
) => {
  const allFlows = await flowiseService.getAvailableFlows(userId);

  // Se é owner da fazenda, mostra todos os flows de fazenda
  if (isOwner) {
    return allFlows.filter((flow) => flow.organizationTypes.includes("farm"));
  }

  // Se não há permissões específicas, retorna flows disponíveis
  if (!userPermissions || userPermissions.length === 0) {
    return allFlows;
  }

  // Mapear manager_type para flow_id
  const managerTypeToFlowId: Record<string, string> = {
    gestor_nutricao: "nutrition",
    gestor_pasto: "pastos",
    gestor_animal: "animal",
    gestor_lote: "batch",
    gestor_manejo_lotes: "batch",
    gestor_financeiro: "finance",
    gestor_nascimento: "lifecycle-born",
    gestor_sanidade: "animal",
  };

  // Extrair os tipos de gestores que o usuário tem permissão
  const allowedManagerTypes = userPermissions.map(
    (permission) => permission.manager_type
  );
  const allowedFlowIds = allowedManagerTypes
    .map((type) => managerTypeToFlowId[type])
    .filter(Boolean);

  // Sempre incluir o master flow
  allowedFlowIds.push("master");

  // Filtrar flows baseado nas permissões
  return allFlows.filter((flow) => allowedFlowIds.includes(flow.id));
};

const getDefaultFlow = async (
  organizationType?: string,
  userPermissions?: any[],
  isOwner?: boolean,
  userId?: string
) => {
  const availableFlows = await getAvailableFlows(
    organizationType,
    userPermissions,
    isOwner,
    userId
  );
  // Return first available flow or null
  return availableFlows.length > 0 ? availableFlows[0] : null;
};

// Helper function to get flow colors
const getFlowColors = (flowId: string) => {
  // Usando o azul principal da aplicação
  if (flowId === "master") {
    // Gestor Master - Azul principal com ícone branco
    return {
      primary: DESIGN_TOKENS.colors.brand.primary, // fundo azul
      light: DESIGN_TOKENS.colors.primary[50],
      border: DESIGN_TOKENS.colors.primary[300] + "30",
      hover: DESIGN_TOKENS.colors.background.elevated,
    };
  } else {
    // Todos os outros gestores - Azul principal da aplicação
    return {
      primary: DESIGN_TOKENS.colors.brand.primary, // #004E64
      light: DESIGN_TOKENS.colors.primary[50], // #E6F3F7
      border: DESIGN_TOKENS.colors.primary[300] + "30",
      hover: DESIGN_TOKENS.colors.background.elevated,
    };
  }
};

/* =========================
   🆕 Helpers para parsing e mapeamento
   ========================= */

// Tenta fazer parse de JSON mesmo se vier "embrulhado" no texto
function tryParseJson<T = any>(maybeJson: string): T | null {
  if (!maybeJson || typeof maybeJson !== "string") return null;
  try {
    return JSON.parse(maybeJson) as T;
  } catch (_) {
    const first = maybeJson.indexOf("{");
    const last = maybeJson.lastIndexOf("}");
    if (first >= 0 && last > first) {
      const slice = maybeJson.slice(first, last + 1);
      try {
        return JSON.parse(slice) as T;
      } catch {
        return null;
      }
    }
    return null;
  }
}

// Novo helper: extrai texto limpo de "resumo" mesmo quando vem como JSON aninhado em string
function extractResumoText(parsed: any): string | undefined {
  if (!parsed) return undefined;
  const r = parsed.resumo;
  if (typeof r === "string") {
    let text = r;
    // Desembrulhar até 3 camadas de JSON aninhado procurando resumo/mensagem
    for (let i = 0; i < 3; i++) {
      const nested = tryParseJson<any>(text);
      if (nested) {
        if (typeof nested === "string") {
          text = nested;
          continue;
        }
        if (typeof nested?.resumo === "string") {
          text = nested.resumo;
          continue;
        }
        if (typeof nested?.message === "string") {
          text = nested.message;
          continue;
        }
      }
      break;
    }
    return text;
  }
  return undefined;
}

// Detecta se a resposta é um erro ou dados inválidos
function isErrorResponse(parsed: any): boolean {
  if (!parsed) return false;

  // Detecta erro quando data é null e resumo contém estruturas técnicas
  if (parsed.data === null && parsed.resumo) {
    const resumo = parsed.resumo;
    // Verifica se o resumo contém estruturas técnicas do LangChain
    if (
      typeof resumo === "string" &&
      (resumo.includes('"lc":') ||
        resumo.includes('"type": "constructor"') ||
        resumo.includes("langchain_core") ||
        resumo.includes("AIMessageChunk") ||
        resumo.includes("estimatedTokenUsage") ||
        resumo.includes("finish_reason"))
    ) {
      return true;
    }
  }

  // Detecta quando a resposta é diretamente um objeto LangChain
  if (
    parsed.lc === 1 &&
    parsed.type === "constructor" &&
    Array.isArray(parsed.id) &&
    parsed.id.includes("langchain_core")
  ) {
    return true;
  }

  // Detecta estruturas do LangChain baseado em propriedades específicas
  if (
    parsed.kwargs &&
    parsed.kwargs.response_metadata &&
    (parsed.kwargs.response_metadata.finish_reason ||
      parsed.kwargs.response_metadata.usage)
  ) {
    return true;
  }

  // Detecta outros tipos de erro
  if (parsed.error || parsed.status === "error") {
    return true;
  }

  return false;
}

// Detecta se a resposta contém opções de ação para o usuário
function isActionRequiredResponse(text: string): boolean {
  const actionKeywords = [
    "Opções:",
    "Qual opção você escolhe?",
    "Cadastrar novo lote",
    "Verificar se o nome está correto",
    "não foi encontrado no sistema",
    "primeiro preciso que ele esteja cadastrado",
  ];

  return actionKeywords.some((keyword) =>
    text.toLowerCase().includes(keyword.toLowerCase())
  );
}

// Detecta se a resposta é uma pergunta aguardando dados adicionais
function isPendingDataResponse(text: string): boolean {
  const pendingKeywords = [
    "Qual é o período de carência em dias?",
    "Qual é a",
    "Informe o",
    "Preciso saber",
    "Me informe",
    "Qual o",
    "Quantos",
    "Quando",
    "Como",
    "Onde",
  ];

  // Verifica se é uma pergunta (termina com ?) e contém palavras-chave
  const isQuestion = text.trim().endsWith("?");
  const containsKeywords = pendingKeywords.some((keyword) =>
    text.toLowerCase().includes(keyword.toLowerCase())
  );

  return isQuestion && containsKeywords;
}

// Converte conteúdo bruto em texto "amigável" para o chat (evita exibir JSON cru)
function getDisplayTextForMessage(raw: string): string {
  // Preserve loading placeholder messages during processing
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t === "Pensando..." || t === "Processando sua solicitação..." || t === "Processando...") {
      return t;
    }
  }
  const parsed = tryParseJson<any>(raw);
  const resumoText = extractResumoText(parsed);

  const urlCandidate = parsed?.metadata?.url || parsed?.url || parsed?.data?.url;
  const tasksCandidate = parsed?.metadata?.tasks || parsed?.tasks || parsed?.data?.tasks;
  

  // Detecta e trata respostas de erro
  if (isErrorResponse(parsed)) {
    return "Desculpe, não consegui processar sua solicitação. Pode tentar reformular sua pergunta?";
  }

  if (parsed?.status === "INCOMPLETO" || parsed?.status === "MISSING_FIELDS") {
    if (resumoText) return resumoText;
    if (parsed?.resumo) return parsed.resumo;
    if (parsed?.data?.resumo) return parsed.data.resumo;
    const mf = (parsed?.missing_fields || parsed?.data?.missing_fields);
    if (Array.isArray(mf) && mf.length > 0) {
      return `Campos faltando: ${mf.join(", ")}.`;
    }
    return "";
  }

  // Verifica se é uma resposta de ordenha/produção
  if (
    parsed?.url === "/ordenha/producao/detalhes" ||
    parsed?.data?.operation_details?.handling_type_code === "registrar_producao"
  ) {
    return parsed.resumo || "Produção de ordenha registrada com sucesso.";
  }

  // Verifica se é uma resposta de consulta de histórico de ordenha do lote
  if (parsed?.url === "/ordenha/busca_lote") {
    return (
      parsed.resumo || "Histórico de ordenha do lote consultado com sucesso."
    );
  }

  // Verifica se é uma resposta de consulta de histórico de ordenha do animal
  if (parsed?.url === "/ordenha/busca_animal") {
    return (
      parsed.resumo || "Histórico de ordenha do animal consultado com sucesso."
    );
  }

  // Verifica se é uma resposta de limpeza de tetos
  if (
    parsed?.url === "/manejo/limpeza-tetos/detalhes" ||
    parsed?.data?.operation_details?.handling_type_code === "registrar_manejo"
  ) {
    return parsed.resumo || "Manejo registrado com sucesso.";
  }

  // Verifica se é uma resposta de tratamento de endoparasitas
  if (
    parsed?.url === "/sanidade/endoparasita/detalhes" ||
    parsed?.data?.handling_type_code === "registrar_endoparasita"
  ) {
    let resumo =
      parsed.resumo ||
      "Tratamento contra endoparasitas registrado com sucesso.";
    // Remove JSON não formatado da data
    resumo = resumo.replace(
      /\{"formattedDate":"[^"]+","timezone":"[^"]+"\}/g,
      parsed?.data?.handling_date?.formattedDate || "na data registrada"
    );
    return resumo;
  }

  // Verifica se é uma resposta de tratamento de ectoparasitas
  if (
    parsed?.url === "/sanidade/ectoparasita/detalhes" ||
    parsed?.data?.handling_type_code === "registrar_ectoparasita"
  ) {
    let resumo =
      parsed.resumo ||
      "Tratamento contra ectoparasitas registrado com sucesso.";
    // Remove JSON não formatado da data
    resumo = resumo.replace(
      /\{"formattedDate":"[^"]+","timezone":"[^"]+"\}/g,
      parsed?.data?.handling_date?.formattedDate || "na data registrada"
    );
    return resumo;
  }

  // Verifica se é uma resposta de histórico de sanidade do lote
  if (parsed?.url === "/sanidade/consultas" && parsed?.data?.nome_do_lote) {
    return (
      parsed.resumo || "Histórico de sanidade do lote consultado com sucesso."
    );
  }

  // Verifica se é uma resposta de vacinação
  if (
    parsed?.url === "/sanidade/vacinacao/detalhes" ||
    parsed?.data?.handling_type_code === "registrar_vacinacao"
  ) {
    let resumo = parsed.resumo || "Vacinação registrada com sucesso.";
    // Remove JSON não formatado da data
    resumo = resumo.replace(
      /\{"formattedDate":"[^"]+","timezone":"[^"]+"\}/g,
      parsed?.data?.handling_date?.formattedDate || "na data registrada"
    );
    return resumo;
  }

  // Verifica se é uma resposta de histórico do animal
  if (
    parsed?.url?.includes("/historico/animal/") ||
    parsed?.data?.operation_details?.historico
  ) {
    return parsed.resumo || "Histórico do animal recuperado com sucesso.";
  }

  // Verifica se é uma resposta de reprodução
  if (
    parsed?.url?.includes("/reproducao/detalhes") ||
    (parsed?.data?.entity_type === "animal" &&
      parsed?.data?.handling_record_confirmation)
  ) {
    return parsed.resumo || "Reprodução registrada com sucesso.";
  }

  // Verifica se é uma resposta de reprodução pendente
  if (parsed?.url?.includes("/reproducao/home") && parsed.data === null) {
    return parsed.resumo || "Operação pendente de informações adicionais.";
  }

  // Verifica se é uma resposta de nascimento
  if (
    parsed?.data?.content_type === "nascimento" ||
    parsed?.resumo?.includes("Nascimento registrado") ||
    parsed?.resumo?.includes("nascimento registrado")
  ) {
    return parsed.resumo || "Nascimento registrado com sucesso.";
  }

  // Verifica se é uma resposta com data null (mostra apenas o resumo limpo)
  if (parsed?.data === null && parsed?.resumo) {
    return resumoText || parsed.resumo;
  }

  // Verifica se é uma resposta de 'não encontrado' ou erro
  if (
    (parsed?.data === null && parsed?.resumo?.includes("não encontrado")) ||
    (parsed?.data?.api_status === "error" &&
      (parsed?.resumo?.includes("não foi encontrado") ||
        parsed?.resumo?.includes("não encontrado")))
  ) {
    return parsed.resumo || "Recurso não encontrado.";
  }

  // Se tem content_type, usa ele para determinar o que mostrar no chat
  // O content_type pode estar em parsed.content_type OU parsed.data.content_type
  const contentType = parsed?.content_type || parsed?.data?.content_type;

  if (contentType) {
    switch (contentType) {
      case "animal_detail":
      case "animal-detail":
        return (
          parsed.resumo || parsed.message || "Consulta de animal processada"
        );
      case "animal_profile_with_nutrition":
        return (
          parsed.resumo ||
          parsed.message ||
          "Histórico nutricional do animal carregado"
        );
      case "lote_profile_with_nutrition":
        return (
          parsed.resumo ||
          parsed.message ||
          "Histórico nutricional do lote carregado"
        );
      case "lote_profile_with_sanity":
        return (
          parsed.resumo ||
          parsed.message ||
          "Histórico de sanidade do lote carregado"
        );
      case "animal_profile_with_sanity":
        return (
          parsed.resumo ||
          parsed.message ||
          "Histórico de sanidade do animal carregado"
        );
      case "animal_list":
      case "animal-list":
        return parsed.resumo || parsed.message || "Lista de animais processada";
      case "nascimento_animal":
        return (
          parsed.data?.resumo ||
          parsed.resumo ||
          parsed.message ||
          "Erro no registro de nascimento"
        );
      default:
        // Para outros content_types, evita exibir JSON cru; usa resumo/mensagem limpos
        return resumoText || parsed?.resumo || parsed?.data?.resumo || parsed?.message || parsed?.data?.message || "";
    }
  }

  // Se o backend manda data.message, priorizamos mostrar isso no chat
  if (parsed?.data?.message && typeof parsed.data.message === "string") {
    return parsed.data.message;
  }

  // Se for lista de lotes, mostra apenas o resumo
  if (parsed?.url === "/Lotes/lista" || parsed?.url === "/lotes/lista") {
    return parsed.resumo || parsed.message || "Lista de lotes carregada";
  }

  // Lista de pastos
  if (parsed?.url === "/pastos/lista") {
    return parsed.resumo || parsed.message || "Lista de pastos carregada";
  }

  // Se for consulta de lote (URL com L maiúsculo), mostra apenas o resumo
  if (
    parsed?.url === "/Lotes/detalhes" ||
    (parsed?.data &&
      parsed.data.cattle_tags &&
      Array.isArray(parsed.data.cattle_tags))
  ) {
    return parsed.resumo || parsed.message || "Detalhes do lote carregados";
  }

  // Se for criação de lote, mostra apenas o resumo
  if (
    parsed?.url === "/lotes/detalhes" &&
    parsed.resumo &&
    parsed.resumo.includes("criado com sucesso")
  ) {
    return parsed.resumo || "Lote criado com sucesso!";
  }

  // Se for adição de animal ao lote (sucesso ou erro), mostra apenas o resumo
  if (
    parsed?.url === "/lotes/detalhes" &&
    parsed.resumo &&
    (parsed.resumo.includes("animal adicionado ao lote") ||
      parsed.resumo.includes("animal adicionados ao lote") ||
      parsed.resumo.includes("animais adicionados ao lote") ||
      parsed.resumo.includes("Animais não encontrados"))
  ) {
    return parsed.resumo;
  }

  // Se for remoção de animal do lote, mostra apenas o resumo
  if (
    parsed?.url === "/lotes/detalhes" &&
    parsed.resumo &&
    parsed.resumo.includes("animais removidos do lote")
  ) {
    return parsed.resumo;
  }

  // Se for nutrição (fallback para URL), mostra apenas o resumo
  if (parsed?.url === "/nutricao/detalhes") {
    return parsed.resumo || parsed.message || "Registro nutricional processado";
  }

  // Se for resposta de permissões, mostra apenas o resumo
  if (
    (parsed?.url === "/permissoes/usuarios" || parsed?.url === "/permissoes") &&
    parsed?.resumo
  ) {
    return parsed.resumo;
  }

  // Se for resposta de sanidade/vacinação, mostra apenas o resumo
  if (parsed?.url === "/sanidade/vacinacao/detalhes" && parsed?.resumo) {
    return parsed.resumo;
  }

  // Se for cadastro de animal, mostra apenas o resumo
  if (parsed?.url === "/animal/detalhes" && parsed?.resumo) {
    return parsed.resumo;
  }

  // Se for histórico nutricional de animal, mostra resumo encurtado
  if (
    (parsed?.url === "/animal/perfil-nutricao" ||
      parsed?.url === "/nutricao/busca_animal") &&
    parsed?.resumo
  ) {
    const resumo = parsed.resumo;

    // Se o resumo tem múltiplos dias, mostra apenas o mais recente + resumo
    if (resumo.includes("No dia") && resumo.split("No dia").length > 2) {
      const sentences = resumo.split(". ");
      const firstSentence = sentences[0] + ".";
      const totalDays = resumo.split("No dia").length - 1;

      if (totalDays > 1) {
        return `${firstSentence} (${totalDays} dias de registros - veja detalhes no painel)`;
      }
    }

    return resumo;
  }

  // Se for histórico nutricional de lote, mostra apenas o resumo
  if (
    (parsed?.url === "/lote/perfil-nutricao" ||
      parsed?.url === "/nutricao/busca_lote") &&
    parsed?.resumo
  ) {
    return parsed.resumo;
  }

  // Se é texto simples que contém "não foi encontrado", mostra resumo encurtado
  if (!parsed && raw.includes("não foi encontrado")) {
    const firstSentence = raw.split(".")[0] + ".";
    return `${firstSentence} Veja opções no painel.`;
  }

  // Se é uma resposta inesperada com operation_code mas sem content_type válido
  if (
    parsed &&
    parsed.operation_code &&
    !parsed.content_type &&
    !parsed.data?.content_type
  ) {
    return resumoText || parsed?.resumo || parsed?.data?.resumo || parsed?.message || parsed?.data?.message || "";
  }

  // Se chegou até aqui e é um objeto JSON, tenta mostrar apenas o resumo
  if (parsed && typeof parsed === "object") {
    return resumoText || parsed?.resumo || parsed?.data?.resumo || parsed?.message || parsed?.data?.message || "";
  }

  return resumoText || parsed?.resumo || parsed?.data?.resumo || parsed?.message || parsed?.data?.message || "";
}

// Sanitiza o texto do chat removendo rótulo "Resposta:" e aspas/backticks
function sanitizeChatText(text: string): string {
  if (!text || typeof text !== "string") return text as any;
  let t = text.trim();

  // Remover "Resposta:" (com variações em markdown)
  t = t.replace(/^\s*(\*\*\s*)?Resposta(\s*\*\*)?\s*[:\-–—]\s*/i, "");

  // Remover rótulos em negrito/itálico/código no início
  t = t.replace(/^\s*\*\*[^*]+:\s*\*\*\s*/i, ""); // **Label:**
  t = t.replace(/^\s*\*\*[^*]+\*\*\s*:?:?\s*/i, ""); // **Label**:
  t = t.replace(/^\s*\*[^*]+:\s*\*\s*/i, "");      // *Label:*
  t = t.replace(/^\s*_[^_]+:\s*_\s*/i, "");        // _Label:_
  t = t.replace(/^\s*`[^`]+`:\s*/i, "");           // `Label`:

  // Remover ocorrência residual "**: **"
  t = t.replace(/^\s*\*\*\s*[:\-–—]\s*\*\*\s*/i, "");

  // Remover aspas/backticks nas pontas
  t = t.replace(/^[`"'“”]+/, "").replace(/[`"'“”]+$/, "");

  // Normaliza espaços
  t = t.replace(/^\s+/, "").replace(/\s+$/g, "");
  return t;
}

function isMarkdownLike(text: string): boolean {
  if (!text) return false;
  const s = text.trim();
  if (!s) return false;
  return /(\n|^)\s*```/.test(s) || /(\n|^)\s*#{1,6}\s+/.test(s) || /(\n|^)\s*[-*]\s+/.test(s) || /\*\*[^*]+\*\*/.test(s) || /`[^`]+`/.test(s);
}

function renderInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let i = 0;
  while (i < text.length) {
    const bold = text.slice(i).match(/^\*\*([^*]+)\*\*/);
    if (bold) {
      nodes.push(
        <Typography
          component="span"
          sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.text.primary }}
        >
          {bold[1]}
        </Typography>
      );
      i += bold[0].length;
      continue;
    }
    const italic = text.slice(i).match(/^\*([^*]+)\*/) || text.slice(i).match(/^_([^_]+)_/);
    if (italic) {
      nodes.push(
        <Typography
          component="span"
          sx={{ fontStyle: 'italic', color: DESIGN_TOKENS.colors.text.primary }}
        >
          {italic[1]}
        </Typography>
      );
      i += italic[0].length;
      continue;
    }
    const strike = text.slice(i).match(/^~~([^~]+)~~/);
    if (strike) {
      nodes.push(
        <Typography
          component="span"
          sx={{ textDecoration: 'line-through', color: DESIGN_TOKENS.colors.text.primary }}
        >
          {strike[1]}
        </Typography>
      );
      i += strike[0].length;
      continue;
    }
    const code = text.slice(i).match(/^`([^`]+)`/);
    if (code) {
      nodes.push(
        <Box
          component="span"
          sx={{
            fontFamily: 'monospace',
            bgcolor: 'action.hover',
            px: 0.75,
            py: 0.25,
            borderRadius: 0.75,
            border: '1px solid',
            borderColor: 'divider',
            fontSize: '0.85em',
          }}
        >
          {code[1]}
        </Box>
      );
      i += code[0].length;
      continue;
    }
    const link = text.slice(i).match(/^\[([^\]]+)\]\(([^\)]+)\)/);
    if (link) {
      nodes.push(
        <Box component="a" href={link[2]} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', textDecoration: 'underline' }}>
          {link[1]}
        </Box>
      );
      i += link[0].length;
      continue;
    }
    nodes.push(text[i]);
    i += 1;
  }
  return nodes;
}

function renderMarkdown(text: string): React.ReactNode {
  const parts: { type: 'code' | 'text'; content: string; lang?: string }[] = [];
  let pos = 0;
  while (pos < text.length) {
    const start = text.indexOf('```', pos);
    if (start === -1) {
      parts.push({ type: 'text', content: text.slice(pos) });
      break;
    }
    if (start > pos) parts.push({ type: 'text', content: text.slice(pos, start) });
    const end = text.indexOf('```', start + 3);
    const segment = end === -1 ? text.slice(start + 3) : text.slice(start + 3, end);
    const m = segment.match(/^([a-zA-Z0-9_-]+)\s*(?:\n|\s)?/);
    let lang: string | undefined = undefined;
    let code = segment;
    if (m) {
      lang = m[1];
      code = segment.slice(m[0].length);
    }
    parts.push({ type: 'code', content: code, lang });
    pos = end === -1 ? text.length : end + 3;
  }

  const normalizeMarkdownInline = (s: string): string => {
    return s
      .replace(/(\S)\s+(#{1,6})\s+/g, '$1\n$2 ')
      .replace(/\s+-\s+\[/g, '\n- [');
  };

  const renderText = (block: string, keyPrefix: string) => {
    const lines = normalizeMarkdownInline(block).split(/\r?\n/);
    const nodes: React.ReactNode[] = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (!line.trim()) { i++; continue; }
      const h = line.match(/^\s*(#{1,6})\s+(.*)$/);
      if (h) {
        const level = h[1].length;
        const textLine = h[2];
        const variant = level === 1 ? 'h5' : level === 2 ? 'h6' : 'subtitle1';
        nodes.push(
          <Typography
            key={`${keyPrefix}-h-${i}`}
            variant={variant}
            sx={{
              fontWeight: 700,
              mt: level === 1 ? 1.5 : 1,
              mb: 0.75,
              color: DESIGN_TOKENS.colors.text.primary,
            }}
          >
            {renderInline(textLine)}
          </Typography>
        );
        i++; continue;
      }
      if (/^\s*(?:-{3,}|_{3,}|\*{3,})\s*$/.test(line)) {
        nodes.push(<Divider key={`${keyPrefix}-hr-${i}`} sx={{ my: 1 }} />);
        i++; continue;
      }
      if (/^\s*>\s?/.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
          items.push(lines[i].replace(/^\s*>\s?/, ''));
          i++;
        }
        nodes.push(
          <Box
            key={`${keyPrefix}-q-${i}`}
            sx={{
              borderLeft: '3px solid',
              borderColor: 'divider',
              bgcolor: 'action.hover',
              borderRadius: 1,
              pl: 1.5,
              pr: 1.5,
              py: 1,
              my: 1.25,
            }}
          >
            {items.map((t, idx) => (
              <Typography
                key={`${keyPrefix}-q-${i}-${idx}`}
                variant="body1"
                sx={{ color: DESIGN_TOKENS.colors.text.primary }}
              >
                {renderInline(t)}
              </Typography>
            ))}
          </Box>
        );
        continue;
      }
      const ulMatch = line.match(/^\s*[-*]\s+(.*)$/);
      const olMatch = line.match(/^\s*(\d{1,3})\.\s+(.*)$/);
      if (ulMatch) {
        const items: string[] = [];
        while (i < lines.length) {
          const m2 = lines[i].match(/^\s*[-*]\s+(.*)$/);
          if (!m2) break;
          items.push(m2[1]);
          i++;
        }
        nodes.push(
          <Box
            key={`${keyPrefix}-ul-${i}`}
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 2, my: 0.75 }}
          >
            {items.map((t, idx) => (
              <Typography
                key={`${keyPrefix}-ul-${i}-${idx}`}
                variant="body1"
                sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', lineHeight: 1.6 }}
              >
                <Box component="span" sx={{ pt: '2px' }}>•</Box>
                <Box component="span" sx={{ flex: 1 }}>{renderInline(t)}</Box>
              </Typography>
            ))}
          </Box>
        );
        continue;
      }
      if (olMatch) {
        const items: {n: string, t: string}[] = [];
        while (i < lines.length) {
          const m2 = lines[i].match(/^\s*(\d{1,3})\.\s+(.*)$/);
          if (!m2) break;
          items.push({ n: m2[1], t: m2[2] });
          i++;
        }
        nodes.push(
          <Box
            key={`${keyPrefix}-ol-${i}`}
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 2, my: 0.75 }}
          >
            {items.map((it, idx) => (
              <Typography
                key={`${keyPrefix}-ol-${i}-${idx}`}
                variant="body1"
                sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', lineHeight: 1.6 }}
              >
                <Box component="span" sx={{ fontWeight: 600, pt: '2px' }}>{it.n}.</Box>
                <Box component="span" sx={{ flex: 1 }}>{renderInline(it.t)}</Box>
              </Typography>
            ))}
          </Box>
        );
        continue;
      }
      const para: string[] = [];
      while (i < lines.length && lines[i].trim() && !/^\s*(#{1,6})\s+/.test(lines[i]) && !/^\s*[-*]\s+/.test(lines[i]) && !/^\s*\d{1,3}\.\s+/.test(lines[i]) && !/^\s*>\s?/.test(lines[i])) {
        para.push(lines[i].trim());
        i++;
      }
      nodes.push(
        <Typography
          key={`${keyPrefix}-p-${i}`}
          variant="body1"
          sx={{
            whiteSpace: 'pre-wrap',
            lineHeight: 1.6,
            mb: 0.75,
            color: DESIGN_TOKENS.colors.text.primary,
          }}
        >
          {renderInline(para.join(' '))}
        </Typography>
      );
    }
    return nodes;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {parts.map((p, idx) => {
        if (p.type === 'code') {
          if ((p.lang || '').toLowerCase() === 'md' || (p.lang || '').toLowerCase() === 'markdown' || (p.lang || '').toLowerCase() === 'mdx' || (p.lang || '').toLowerCase() === 'text') {
            return <Box key={`md-${idx}`}>{renderMarkdown(p.content)}</Box>;
          }
          return (
            <Box
              key={`code-${idx}`}
              sx={{
                bgcolor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
                p: 1.5,
                overflowX: 'auto',
              }}
            >
              <Typography
                component="pre"
                sx={{ m: 0, fontFamily: 'monospace', whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}
              >
                {p.content}
              </Typography>
            </Box>
          );
        }
        return <Box key={`text-${idx}`}>{renderText(p.content, `t-${idx}`)}</Box>;
      })}
    </Box>
  );
}
function stripMarkdown(text: string): string {
  if (!text || typeof text !== "string") return text as any;
  let t = text;
  t = t.replace(/```[a-zA-Z0-9_-]*\s*/g, "");
  t = t.replace(/```/g, "");
  t = t.replace(/`([^`]+)`/g, "$1");
  t = t.replace(/__([^_]+)__/g, "$1");
  t = t.replace(/\*\*([^*]+)\*\*/g, "$1");
  t = t.replace(/\*([^*]+)\*/g, "$1");
  t = t.replace(/_([^_]+)_/g, "$1");
  t = t.replace(/^\s{0,3}#{1,6}\s+/gm, "");
  t = t.replace(/(^|\s)#{1,6}(?=\s|[.,;:!?]|$)/g, "$1");
  t = t.replace(/^\s{0,3}>\s?/gm, "");
  t = t.replace(/(^|\s)>\s?/g, "$1");
  t = t.replace(/^\s{0,3}[\-\*\+]\s+\[[x\s]\]\s+/gmi, "");
  t = t.replace(/^\s{0,3}[\-\*\+]\s+/gm, "");
  t = t.replace(/(^|\s)[\-\*\+]\s+/g, "$1");
  t = t.replace(/^\s{0,3}\d{1,3}[\.)]\s+/gm, "");
  t = t.replace(/(^|\s)\d{1,3}[\.)]\s+/g, "$1");
  t = t.replace(/!\[([^\]]*)\]\([^\)]+\)/g, "$1");
  t = t.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, "$1");
  t = t.replace(/<https?:\/\/[^>]+>/g, (m) => m.slice(1, -1));
  t = t.replace(/^\s*(?:-{3,}|_{3,}|\*{3,})\s*$/gm, "");
  t = t.replace(/\\([\\`*_\{\}\[\]\(\)#\+.\-!])/g, "$1");
  t = t.replace(/\n{3,}/g, "\n\n");
  t = t.trim();
  return t;
}

// Detecta se a resposta representa "consulta de animal" (novo OU antigo formato)
function isAnimalLookupPayload(parsed: any): boolean {
  if (!parsed || !parsed.data) return false;
  const d = parsed.data;
  return (
    d?.operation_code === "consulta_animal" || // compat com formato antigo
    !!d?._id ||
    !!d?.cattle_id ||
    (typeof d?.count === "number" && !Array.isArray(d)) // animal individual com count
  );
}

function isAnimalListPayload(parsed: any): boolean {
  if (!parsed || !parsed.data) return false;
  const d = parsed.data;
  return (
    Array.isArray(d) && // é um array
    d.length > 0 && // tem pelo menos um item
    d.some((item: any) => item?._id || item?.cattle_id || item?.name) // itens parecem ser animais
  );
}

// Normaliza registros brutos de nutrição para o contrato visual de LotNutritionHistoryView
function normalizeLotNutritionRecords(records: any[]): {
  _id: string;
  _key: string;
  nome_do_lote: string;
  total_de_animais: number;
  animais: string[];
  lancamentos_do_lote: {
    data: string;
    alimento: string;
    quantidade_total_kg: number;
  }[];
} {
  const first = records[0] || {};
  const nome_do_lote =
    first?.nome_do_lote ||
    first?.operation_lot_id ||
    records.find((r: any) => !!r?.nome_do_lote)?.nome_do_lote ||
    "Lote";
  const animaisSet = new Set<string>();
  records.forEach((r: any) => {
    if (r?.cattle_tag) animaisSet.add(r.cattle_tag);
    if (Array.isArray(r?.cattle_tags))
      r.cattle_tags.forEach((tag: string) => animaisSet.add(tag));
  });
  const animais = Array.from(animaisSet);

  const lancamentos_do_lote = records.map((r: any) => ({
    data: r?.handling_date || r?.date || r?.data || "",
    alimento:
      r?.handling_details?.feed_type ||
      r?.feed_type ||
      r?.alimento ||
      "não informado",
    quantidade_total_kg:
      (typeof r?.handling_details?.quantity_kg === "number"
        ? r.handling_details.quantity_kg
        : undefined) ??
      (typeof r?.quantity_kg === "number" ? r.quantity_kg : undefined) ??
      (typeof r?.quantidade_total_kg === "number"
        ? r.quantidade_total_kg
        : undefined) ??
      0,
  }));

  return {
    _id: first?._id || "",
    _key: first?._key || "",
    nome_do_lote,
    total_de_animais:
      animais.length ||
      (typeof first?.total_de_animais === "number"
        ? first.total_de_animais
        : 0),
    animais,
    lancamentos_do_lote,
  };
}

// Mapeia o payload do backend para o contrato visual do AnimalPassport
function mapApiAnimalToPassportData(apiData: any) {
  // Escolha de id: usa o tag do brinco, senão _key/_id/name
  const id =
    apiData?.cattle_id?.tag ??
    apiData?._key ??
    apiData?._id ??
    apiData?.name ??
    "";

  return {
    // IDs e identificação
    id,
    _id: apiData?._id,
    _key: apiData?._key,
    name: apiData?.name, // ex: "892"

    // Identificação do gado
    cattle_id: apiData?.cattle_id
      ? {
          tag: apiData.cattle_id.tag,
          country_code: apiData.cattle_id.country_code,
          manufacturer_code: apiData.cattle_id.manufacturer_code,
          unique_id: apiData.cattle_id.unique_id,
        }
      : undefined,

    // Informações básicas
    breed: apiData?.breed,
    breed_code: apiData?.breed_code, // ex: "nelore"
    gender: apiData?.gender, // "Macho" / "Fêmea"

    // Datas e peso
    birth_date: apiData?.birth_date, // ex: "2023-01-01"
    birth_weight_kg: apiData?.birth_weight_kg, // ex: 294

    // Localização
    farm_id: apiData?.farm_id, // ex: "farm_001"
    birth_farm_id: apiData?.birth_farm_id,
    current_farm_id: apiData?.current_farm_id,
    current_paddock_id: apiData?.current_paddock_id, // ex: "pasto 1"

    // Status
    current_status: apiData?.current_status, // ex: "active"
    operation_status: apiData?.operation_status, // ex: "COMPLETO"

    // Timestamps
    created_at: apiData?.created_at,
    updated_at: apiData?.updated_at,

    // Mensagem da API
    message: apiData?.message, // "Animal encontrado com sucesso"
    count: apiData?.count,
    resumo: apiData?.resumo,
    url: apiData?.url,

    // Campos legados (manter compatibilidade)
    status: apiData?.operation_status || apiData?.current_status,
    location: apiData?.current_paddock_id || apiData?.current_farm_id,
    weight:
      typeof apiData?.birth_weight_kg === "number"
        ? `${apiData.birth_weight_kg} kg (nascimento)`
        : undefined,

    // Histórico de manejo
    handling_history: apiData?.handling_history || [],

    // Biometria do focinho (imagem base64 e metadados)
    muzzle_biometrics: apiData?.muzzle_biometrics,

    // Campos que não temos dados ainda
    age: undefined, // será calculado no componente
    birthDate: apiData?.birth_date, // compatibilidade
    owner: undefined,
    lastCheckup: undefined,
    healthScore: undefined,
    vaccinations: undefined,
    reproductiveHistory: undefined,
  };
}

// Mapeia o payload do backend para o contrato visual do ExamDetail
function mapApiExamToDetailData(apiData: any) {
  

  // Extrai a lista de exames do campo 'data'
  const examsList = apiData?.data || [];
  

  // Mapeia cada exame para o formato esperado pelo componente
  const mappedExams = examsList.map((examData: any, index: number) => {
    

    const handlingDetails = examData?.handling_details || {};

    // Tenta parsear examData caso venha como string JSON com campos extras
    const examDataStr = handlingDetails?.examData;
    let parsedExtraExamData: any = undefined;
    if (typeof examDataStr === "string") {
      try {
        parsedExtraExamData = JSON.parse(examDataStr);
      } catch (e) {
        console.warn("mapApiExamToDetailData: falha ao parsear examData", e);
      }
    } else if (typeof examDataStr === "object" && examDataStr !== null) {
      parsedExtraExamData = examDataStr;
    }

    return {
      // IDs e identificação
      _id: examData?._id,
      _key: examData?._key,
      _rev: examData?._rev,

      // Informações da fazenda e animal
      farm_id: examData?.farm_id,
      cattle_tag: examData?.cattle_tag,

      // Tipo e detalhes do manejo
      handling_type_code: examData?.handling_type_code,
      handling_date: examData?.handling_date,
      handling_time: examData?.handling_time,

      // Detalhes específicos do exame - os dados estão diretamente em handling_details
      handling_details: {
        exam_type: handlingDetails.exam_type,
        plate_id: handlingDetails.plate_id,
        collection_date: handlingDetails.collection_date,
        exam_date: handlingDetails.exam_date,
        attachments: handlingDetails.attachments,
        // Resultados estão em handling_details.results
        results: {
          udderSigns: handlingDetails.results?.udderSigns,
          teatsAffected: handlingDetails.results?.teatsAffected,
          exam_result: handlingDetails.results?.exam_result,
          staphPrimary: handlingDetails.results?.staphPrimary,
          staphSecondary: handlingDetails.results?.staphSecondary,
          strepPrimary: handlingDetails.results?.strepPrimary,
          strepSecondary: handlingDetails.results?.strepSecondary,
          generalPrimary: handlingDetails.results?.generalPrimary,
          generalSecondary: handlingDetails.results?.generalSecondary,
          milkAspect: handlingDetails.results?.milkAspect,
          observation: handlingDetails.results?.observation,
          // Campos adicionais de tratamento
          situation: handlingDetails.results?.situation ?? parsedExtraExamData?.situation ?? handlingDetails.treatment?.situation ?? undefined,
          mainTreatment: handlingDetails.results?.mainTreatment ?? parsedExtraExamData?.mainTreatment ?? parsedExtraExamData?.treatmentPlan?.principal ?? handlingDetails.treatment?.mainTreatment ?? undefined,
          supportiveTreatment: handlingDetails.results?.supportiveTreatment ?? parsedExtraExamData?.supportiveTreatment ?? parsedExtraExamData?.treatmentPlan?.suporte ?? handlingDetails.treatment?.supportiveTreatment ?? undefined,
          recommendedAntibiotics: handlingDetails.results?.recommendedAntibiotics ?? parsedExtraExamData?.recommendedAntibiotics ?? parsedExtraExamData?.treatmentPlan?.antibioticos ?? handlingDetails.treatment?.recommendedAntibiotics ?? undefined,
          treatmentPlan: handlingDetails.results?.treatmentPlan ?? parsedExtraExamData?.treatmentPlan ?? undefined,
          aines: handlingDetails.results?.aines ?? parsedExtraExamData?.aines ?? parsedExtraExamData?.treatmentPlan?.aines ?? handlingDetails.treatment?.aines ?? undefined,
        },
      },

      // Metadados
      notes: examData?.notes,
      cost: examData?.cost,
      created_at: examData?.created_at,
      updated_at: examData?.updated_at,
    };
  });

  const result = {
    exams: mappedExams,
    totalExams: mappedExams.length,
    resumo: apiData?.resumo,
  };

  
  return result;
}

/* =========================
   Componentes de UI
   ========================= */

const MessageBubble: React.FC<{
  message: ChatMessage;
  organization?: Organization;
  selectedFlow?: string;
}> = React.memo(({ message, organization, selectedFlow }) => {
  const isUser = message.sender === "user";
  const { t } = useTranslation(["common"]);
  const { sendMessage, isMultiTaskRunning, handleRetryDecision, retrySingleMessage } = useChatContext();
  const [singleRetryDismissed, setSingleRetryDismissed] = useState(false);
  const [multiTaskRetryDismissed, setMultiTaskRetryDismissed] = useState(false);
  const [multiTaskRetryLoading, setMultiTaskRetryLoading] = useState(false);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Memoizar cálculos pesados
  const orgColor = useMemo(
    () => getOrganizationColor(organization?.type || "other"),
    [organization?.type]
  );

  // Função para obter a cor específica do gestor baseada no messageFlowId
  const getManagerColor = useCallback((flowId: string) => {
    const flowColors = getFlowColors(flowId);
    return flowColors.primary;
  }, []);

  const displayContent = useMemo(() => {
    if (isUser || message.sender_type === "user") {
      return message.content;
    }
    if (message.status === "sending" && (message.sender === "assistant" || message.sender_type === "assistant")) {
      return message.content;
    }
    const parsed = tryParseJson<any>(message.content);
    const urlCandidate = parsed?.metadata?.url || parsed?.url || parsed?.data?.url;
    const tasksCandidate = parsed?.metadata?.tasks || parsed?.tasks || parsed?.data?.tasks;
    if (typeof urlCandidate === "string" && urlCandidate.toLowerCase().includes("master/tasks") && Array.isArray(tasksCandidate) && tasksCandidate.length > 0) {
      if (message.status === "sending" || (isMultiTaskRunning && !extractResumoText(parsed))) {
        return "Iniciando execução de múltiplas tarefas...";
      }
      const resumoText = extractResumoText(parsed);
      return sanitizeChatText(resumoText || "");
    }
    return sanitizeChatText(getDisplayTextForMessage(message.content));
  }, [message.content, isUser, message.sender_type, message.status, message.sender, isMultiTaskRunning]);

  const contentToShow = useMemo(() => {
    const trimmed = (displayContent || "").trim();

    const isWelcomeMessage = typeof message?.message_id === 'string' && message.message_id.startsWith('welcome_')
      || typeof message?.id === 'string' && (message.id as string).startsWith('welcome_');

    if (isWelcomeMessage) {
      const orgName = (organization?.name || '').trim();
      return orgName
        ? t('chat.welcomeBubble', { organizationName: orgName })
        : t('chat.welcomeBubbleShort');
    }

    if (trimmed) return trimmed;
    if (!isUser) {
      if (message?.status === 'sending') {
        return 'Processando sua solicitação...';
      }
      return 'Desculpe, ocorreu um erro ao processar sua solicitação.';
    }
    return '';
  }, [displayContent, isUser, t, message?.message_id, message?.id, message?.status, organization?.name]);

  // Função para converter sender_id de volta para flow ID para o ícone
  const getFlowIdFromSenderId = useCallback((senderId: string) => {
    const senderToFlowMap: Record<string, string> = {
      'gestor_ordenha': 'dairy',
      'gestor_perfil_animal': 'animal',
      'gestor_sanidade': 'health',
      'gestor_nutricao': 'nutrition',
      'gestor_permissoes': 'permissions',
      'gestor_lotes': 'batch',
      'gestor_pastos': 'pastos',
      'gestor_reproducao': 'reproducao',
      'gestor_laboratorial': 'laboratory',
      'gestor_master': 'master'
    };
    
    return senderToFlowMap[senderId] || senderId;
  }, []);

  // Usar o sender_id da mensagem para determinar o ícone, em vez do selectedFlow global
  const messageFlowId = useMemo(() => {
    if (isUser) return selectedFlow || "master";
    
    // Para mensagens do assistente, usar o sender_id da mensagem
    const senderId = message.sender_id || "muu_assistant";
    return getFlowIdFromSenderId(senderId);
  }, [isUser, message.sender_id, selectedFlow, getFlowIdFromSenderId]);

  // Cores e ícone do gestor (para uso dentro do balão das respostas)
  const managerColors = useMemo(() => getFlowColors(messageFlowId), [messageFlowId]);
  const iconInBubble = useMemo(() => {
    const icon = getFlowIcon(messageFlowId);
    const isLotManager = messageFlowId === "batch" || messageFlowId === "lotes";
    const iconSize = isLotManager ? 32 : 16;
    
    if (icon.type === "img") {
      return React.cloneElement(icon, {
        style: {
          ...icon.props.style,
          width: iconSize,
          height: iconSize,
          filter: undefined,
        },
      });
    }
    return React.cloneElement(icon, {
      sx: { fontSize: iconSize, color: managerColors.primary },
    });
  }, [messageFlowId, managerColors.primary]);

  const parsedForSuggestion = useMemo(() => tryParseJson<any>(message.content), [message.content]);
  const suggestedPhrase = useMemo(() => {
    if (!parsedForSuggestion || parsedForSuggestion.url !== "/master/chat_user") return null;
    const r = typeof parsedForSuggestion.resumo === "string" ? parsedForSuggestion.resumo : (typeof parsedForSuggestion?.data?.resumo === "string" ? parsedForSuggestion.data.resumo : null);
    if (!r) return null;
    const m = r.match(/:\s*["“]([^"”]+)["”]/);
    return m ? m[1] : null;
  }, [parsedForSuggestion]);

  const getStatusIcon = useCallback(() => {
    switch (message.status) {
      case "sent":
        return (
          <CheckCircle
            sx={{
              fontSize: 14,
              color: isUser ? "rgba(255,255,255,0.8)" : STATUS_COLORS.success,
            }}
          />
        );
      case "sending":
        return (
          <Schedule
            sx={{
              fontSize: 14,
              color: isUser ? "rgba(255,255,255,0.8)" : STATUS_COLORS.warning,
            }}
          />
        );
      case "error":
        return (
          <Error
            sx={{
              fontSize: 14,
              color: isUser ? "rgba(255,255,255,0.8)" : STATUS_COLORS.error,
            }}
          />
        );
      default:
        return null;
    }
  }, [message.status, isUser]);

  const shouldShowInlineResults = (parsed: any) => {
    if (!isMobile) return false;
    if (!parsed) return false;
    
    // Verificações existentes
    if (parsed.content_type) return true;
    if (parsed.data?.content_type) return true;
    if (parsed.title) return true;
    
    // Nova verificação para histórico de ordenha (mais robusta)
    // Verifica em múltiplos locais onde a URL pode estar
    const url = parsed.url || parsed.data?.url || parsed.metadata?.url;
    if (url && typeof url === 'string') {
      const lowerUrl = url.toLowerCase();
      if (lowerUrl.includes("/ordenha/busca_animal")) return true;
      if (url === "/Lotes/lista" || url === "/lotes/lista") return true;
    }
    
    return false;
  };

  const getInlineResultsType = (parsed: any) => {
    const url = parsed.url || parsed.data?.url || parsed.metadata?.url;
    if (url && typeof url === 'string') {
      const lowerUrl = url.toLowerCase();
      if (lowerUrl.includes("/ordenha/busca_animal")) return 'animalMilkingHistory';
      if (url === "/Lotes/lista" || url === "/lotes/lista") return 'lotList';
    }
    
    return parsed.content_type || parsed.data?.content_type || parsed.title;
  };

  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: "flex",
          justifyContent: isUser ? "flex-end" : "flex-start",
          mb: 2,
          alignItems: "flex-end",
          gap: 1,
        }}
      >
        {/* Ícone do gestor movido para dentro do balão nas respostas */}

        {!isUser ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Paper
              elevation={1}
              sx={{
                ...COMMON_STYLES.messageBubbleBase,
                ...COMMON_STYLES.botMessageBubble,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    bgcolor: managerColors.light,
                    border: `1px solid ${managerColors.border}`,
                    boxShadow: `0 2px 8px ${managerColors.primary}22`,
                    mt: 0.25,
                    flexShrink: 0,
                  }}
                >
                  {iconInBubble}
                </Box>
                <Box sx={{ flex: 1 }}>
                  {isMarkdownLike(contentToShow)
                    ? renderMarkdown(contentToShow)
                    : (
                      <Typography
                        variant="body1"
                        sx={{
                          lineHeight: 1.5,
                          color: 'inherit',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {contentToShow}
                      </Typography>
                    )}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mt: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* sem horário para bot no lado esquerdo */}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {contentToShow && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          color: DESIGN_TOKENS.colors.text.secondary,
                          fontSize: "0.7rem",
                        }}
                      >
                        {new Date(message.timestamp).toLocaleTimeString("pt-BR", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </Typography>
                      <SpeechButton text={contentToShow} size="small" />
                    </>
                  )}
                </Box>
              </Box>



              {/* Mobile Inline Results - Exibe cards compactos em telas pequenas */}
              {!isUser && isMobile && parsedForSuggestion && shouldShowInlineResults(parsedForSuggestion) && (
                 <MobileInlineResults 
                   data={parsedForSuggestion} 
                   type={getInlineResultsType(parsedForSuggestion)} 
                 />
              )}
            </Paper>

            {suggestedPhrase && !suggestionDismissed && (
              <Box sx={{ mt: 0.75, pl: 1, maxWidth: COMMON_STYLES.botMessageBubble.maxWidth, alignSelf: 'flex-start' }}>
                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  Você quer fazer isso: {`"${suggestedPhrase}"`}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                <Button
                  variant="text"
                  onClick={() => sendMessage(suggestedPhrase, selectedFlow || undefined)}
                  sx={{
                    minWidth: 0,
                    px: 0.75,
                    py: 0.25,
                    fontSize: '0.72rem',
                    borderRadius: 1,
                    transition: 'background-color 0.15s ease-in-out',
                    '&:hover': { bgcolor: alpha(managerColors.primary, 0.12) }
                  }}
                >
                  Sim
                </Button>
                <Button
                  variant="text"
                  onClick={() => setSuggestionDismissed(true)}
                  sx={{
                    minWidth: 0,
                    px: 0.75,
                    py: 0.25,
                    fontSize: '0.72rem',
                    borderRadius: 1,
                    transition: 'background-color 0.15s ease-in-out',
                    '&:hover': { bgcolor: alpha(managerColors.primary, 0.12) }
                  }}
                >
                  Não
                </Button>
                </Box>
              </Box>
            )}

            {!isUser && message.status === 'error' && ((message.metadata as any)?.isMultitaskTask || isMultiTaskRunning) && !!(message.metadata as any)?.multitaskTaskContent && !multiTaskRetryDismissed && (
              <Box sx={{ mt: 0.75, pl: 1, maxWidth: COMMON_STYLES.botMessageBubble.maxWidth, alignSelf: 'flex-start' }}>
                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  Deseja tentar novamente?
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                  {multiTaskRetryLoading ? (
                    <CircularProgress size={20} />
                  ) : (
                    <>
                      <Button
                        variant="text"
                        onClick={() => {
                          setMultiTaskRetryLoading(true);
                          handleRetryDecision('yes', (message.metadata as any)?.multitaskTaskContent, (message.metadata as any)?.multitaskTaskIndex, true);
                          setMultiTaskRetryDismissed(true);
                        }}
                        sx={{
                          minWidth: 0,
                          px: 0.75,
                          py: 0.25,
                          fontSize: '0.72rem',
                          borderRadius: 1,
                          transition: 'background-color 0.15s ease-in-out',
                          '&:hover': { bgcolor: alpha(managerColors.primary, 0.12) }
                        }}
                      >
                        Sim
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => {
                          handleRetryDecision('no', (message.metadata as any)?.multitaskTaskContent, (message.metadata as any)?.multitaskTaskIndex, false);
                          setMultiTaskRetryDismissed(true);
                        }}
                        sx={{
                          minWidth: 0,
                          px: 0.75,
                          py: 0.25,
                          fontSize: '0.72rem',
                          borderRadius: 1,
                          transition: 'background-color 0.15s ease-in-out',
                          '&:hover': { bgcolor: alpha(managerColors.primary, 0.12) }
                        }}
                      >
                        Não
                      </Button>
                    </>
                  )}
                </Box>
              </Box>
            )}
            {!isUser && message.status === 'error' && !((message.metadata as any)?.isMultitaskTask) && !singleRetryDismissed && (
              <Box sx={{ mt: 0.75, pl: 1, maxWidth: COMMON_STYLES.botMessageBubble.maxWidth, alignSelf: 'flex-start' }}>
                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  Deseja tentar novamente?
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
                  <Button
                    variant="text"
                    onClick={() => retrySingleMessage('yes', (message.id || message.message_id) as string)}
                    sx={{
                      minWidth: 0,
                      px: 0.75,
                      py: 0.25,
                      fontSize: '0.72rem',
                      borderRadius: 1,
                      transition: 'background-color 0.15s ease-in-out',
                      '&:hover': { bgcolor: alpha(managerColors.primary, 0.12) }
                    }}
                  >
                    Sim
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setSingleRetryDismissed(true)}
                    sx={{
                      minWidth: 0,
                      px: 0.75,
                      py: 0.25,
                      fontSize: '0.72rem',
                      borderRadius: 1,
                      transition: 'background-color 0.15s ease-in-out',
                      '&:hover': { bgcolor: alpha(managerColors.primary, 0.12) }
                    }}
                  >
                    Não
                  </Button>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Paper
            elevation={1}
            sx={{
              ...COMMON_STYLES.messageBubbleBase,
              ...COMMON_STYLES.userMessageBubble,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.5,
                color: "white",
                whiteSpace: "pre-wrap",
              }}
            >
              {contentToShow}
            </Typography>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "0.7rem",
                  }}
                >
                  {new Date(message.timestamp).toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {getStatusIcon()}
              </Box>
            </Box>
          </Paper>
        )}

        {isUser && (
          <Avatar
            sx={{
              bgcolor: BRAND_COLORS.primary,
              ...AVATAR_SIZES.sm,
              mb: SPACING.xs,
            }}
          >
            <Person sx={{ fontSize: 18 }} />
          </Avatar>
        )}
      </Box>
    </Fade>
  );
});

export const MessageView: React.FC<MessageViewProps> = React.memo(
  ({ sessionId, onBackClick }) => {
    const { sessions, organizations, messages, sendMessage, isProcessing, isChatDisabled, isMultiTaskRunning } =
      useChatContext();
    const { authState } = useAuthContext();
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(event.target.value);
      },
      []
    );
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { updateContent, contentView, restorePreviousContent } = useDashboard();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [openMobileExams, setOpenMobileExams] = useState(false);
    const handleCloseMobileExams = useCallback(() => {
      setOpenMobileExams(false);
      restorePreviousContent();
    }, [restorePreviousContent]);

    useEffect(() => {
      if (!isMobile) {
        setOpenMobileExams(false);
        return;
      }
      if (isLabView(contentView)) {
        setOpenMobileExams(true);
      } else {
        setOpenMobileExams(false);
      }
    }, [isMobile, contentView]);

    // Estado para painel de gestores via botão "+"
    const [showGestores, setShowGestores] = useState(false);
    // Flag: ícone do botão usa gestor somente após seleção manual
    const [hasManualFlowSelection, setHasManualFlowSelection] = useState(false);

    const session = useMemo(
      () => sessions.find((s) => s.session_id === sessionId),
      [sessions, sessionId]
    );

    const organization = useMemo(
      () => organizations.find((org) => org.id === session?.organization_id),
      [organizations, session?.organization_id]
    );

    const sessionMessages = useMemo(
      () => messages[sessionId] || [],
      [messages, sessionId]
    );

    // Verificar se o usuário é owner da fazenda e obter suas permissões
    const { userPermissions, isOwner } = useMemo(() => {
      if (!organization || !authState.user) {
        return { userPermissions: [], isOwner: false };
      }

      // Verificar se é owner da fazenda
      const isUserOwner =
        (organization as any).owner_id === authState.profileId;

      if (isUserOwner) {
        return { userPermissions: [], isOwner: true };
      }

      // Buscar o usuário nos colaboradores da fazenda
      const collaborator = (organization as any).collaborators?.find(
        (collab: any) => collab.user_name === authState.user?.username
      );

      if (collaborator) {
        return {
          userPermissions: collaborator.manager_assignments || [],
          isOwner: false,
        };
      }

      return {
        userPermissions: [],
        isOwner: false,
      };
    }, [organization, authState.user, authState.profileId]);

    // Flow selector state - Fixed type to match string value from Select
    const [selectedFlow, setSelectedFlow] = useState<string>("");
    // Switch fixo: quando ativo, sempre envia ao Master e desabilita seleção de outros gestores
    const [forceMasterEnabled] = useState<boolean>(true);
    const [lastAppliedAssistantId, setLastAppliedAssistantId] = useState<string | null>(null);
    const [availableFlows, setAvailableFlows] = useState<any[]>([]);

    // Detecta se o gestor laboratorial está selecionado
    const selectedFlowObj = useMemo(
      () => availableFlows.find((f) => f.id === selectedFlow),
      [availableFlows, selectedFlow]
    );

    const isLaboratorialSelected = useMemo(() => {
      const id = (selectedFlowObj?.id || "").toLowerCase();
      const name = (selectedFlowObj?.name || "").toLowerCase();
      return id === "gestor_laboratorial" || name.includes("laboratorial");
    }, [selectedFlowObj]);

    useEffect(() => {
      const labViews = new Set([
        'examsHub',
        'mastitisIntro',
        'mastitisTeatStep',
        'mastitisAnimalIdStep',
        'mastitisTestStep1',
        'mastitisTestStep2',
        'mastitisTestStep3',
        'exam-list',
        'exam-detail',
      ]);
      if (!labViews.has(contentView) && selectedFlow === 'laboratory') {
        setSelectedFlow('master');
        setHasManualFlowSelection(false);
      }
    }, [contentView, selectedFlow]);

    // Update selected flow when organization changes or permissions change
    useEffect(() => {
      const loadFlowsAndDefault = async () => {
        const farmType = Array.isArray(organization?.farm_type)
          ? organization.farm_type[0]
          : organization?.farm_type;
        const userId = authState.profileId;

        if (!userId) {
          console.warn("No userId available for fetching flows");
          return;
        }

        // Load available flows
        const flows = await getAvailableFlows(
          farmType,
          userPermissions,
          isOwner,
          userId
        );
        setAvailableFlows(flows);

        // Sempre manter 'master' selecionado quando o switch estiver ativo
        if (forceMasterEnabled || !selectedFlow) {
          setSelectedFlow("master");
        }
      };
      loadFlowsAndDefault();
    }, [
      organization?.farm_type,
      userPermissions,
      isOwner,
      authState.profileId,
      forceMasterEnabled,
    ]);

    // Auto-seleciona o gestor utilizado na última resposta do assistente
    // Aplica somente quando chega uma nova mensagem do assistente (evita flicker)
    useEffect(() => {
      const lastAssistant = [...sessionMessages].reverse().find(
        (m) => m.sender_type === "assistant"
      );
      if (!lastAssistant) return;

      const usedId = (lastAssistant?.metadata as any)?.flowUsedId as
        | string
        | undefined;
      const assistantId = lastAssistant.message_id || lastAssistant.id || null;

      // Quando fixado no Master, não alterar a seleção automática
      if (!forceMasterEnabled && assistantId && assistantId !== lastAppliedAssistantId && usedId && usedId !== selectedFlow) {
        setSelectedFlow(usedId);
        setLastAppliedAssistantId(assistantId);
      }
    }, [sessionMessages, lastAppliedAssistantId, selectedFlow, forceMasterEnabled]);

    // Cleanup timeout on unmount - removed as no longer needed

    useEffect(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [sessionMessages]);

    // Foca input ao trocar de sessão
    useEffect(() => {
      inputRef.current?.focus();
    }, [sessionId]);

    // 🆕 Observa quando está processando e mostra loading no painel direito
    const updateContentRef = useRef(updateContent);
    updateContentRef.current = updateContent;

    useEffect(() => {
      const msgs = messages[sessionId] || [];
      const lastMessage = msgs.length ? msgs[msgs.length - 1] : null;
      const lastMeta = (lastMessage?.metadata || {}) as any;
      const lastParsed = tryParseJson<any>(lastMessage?.content || "");
      const lastUrl = lastParsed?.metadata?.url || lastParsed?.url || lastParsed?.data?.url;
      const lastIsMasterTasks = typeof lastUrl === 'string' && lastUrl.toLowerCase().includes('master/tasks');
      const blockRouting = contentView === 'multiTaskProgress' && (isMultiTaskRunning || !!lastMeta?.isMultitaskTask || lastIsMasterTasks);
      if (blockRouting) return;
      if (isProcessing) {
        updateContentRef.current("loading", {
          message: "Processando sua solicitação...",
        });
      } else if (lastMessage?.status === "error") {
        updateContentRef.current("welcome", {});
      }
    }, [isProcessing, messages, sessionId, contentView, isMultiTaskRunning]);

    // 🆕 Observa a última mensagem do assistente e abre o painel da direita baseado no content_type

    useEffect(() => {
      const msgs = messages[sessionId] || [];
      const lastMessage = msgs.length ? msgs[msgs.length - 1] : null;
      const lastMeta = (lastMessage?.metadata || {}) as any;
      const lastParsed = tryParseJson<any>(lastMessage?.content || "");
      const lastUrl = lastParsed?.metadata?.url || lastParsed?.url || lastParsed?.data?.url;
      const lastIsMasterTasks = typeof lastUrl === 'string' && lastUrl.toLowerCase().includes('master/tasks');
      const blockRouting = contentView === 'multiTaskProgress' && (isMultiTaskRunning || !!lastMeta?.isMultitaskTask || lastIsMasterTasks);
      if (blockRouting) return;
      if (!sessionId) return;
      if (!msgs.length) return;

      // Otimização: verificar apenas as últimas 10 mensagens para encontrar a última do assistente
      const recentMsgs = msgs.slice(-10);
      const lastAssistant = [...recentMsgs]
        .reverse()
        .find((m) => m.sender === "assistant");
      if (!lastAssistant || !lastAssistant.content) return;

      const parsed = tryParseJson<any>(lastAssistant.content);

      // Se é texto simples que contém "não foi encontrado", abre tela de not found
      if (!parsed && lastAssistant.content.includes("não foi encontrado")) {
        const notFoundData = {
          data: null,
          resumo: lastAssistant.content,
          url: "",
          mensagem: lastAssistant.content,
        };
        updateContentRef.current("notFound", notFoundData);
        return;
      }

      if (!parsed) return;

      // Se é uma resposta de erro, limpa o loading e não abre painel
      if (isErrorResponse(parsed)) {
        updateContentRef.current("welcome", {});
        return;
      }

      // Se o status é INCOMPLETO ou MISSING_FIELDS, mostra a tela de operação pendente
      if (
        parsed?.status === "INCOMPLETO" ||
        parsed?.status === "MISSING_FIELDS"
      ) {
        updateContentRef.current("pendingOperation", parsed);
        return;
      }

      // Se é uma pergunta aguardando dados adicionais, mostra PendingOperationView
      if (isPendingDataResponse(lastAssistant.content)) {
        const pendingData = {
          operation_code: "registrar_medicacao", // ou outro código baseado no contexto
          status: "AGUARDANDO_DADOS",
          resumo: lastAssistant.content,
          proximo_campo: lastAssistant.content,
          campos_faltantes: [lastAssistant.content],
        };
        updateContentRef.current("pendingOperation", pendingData);
        return;
      }

      // DEBUG: Log detalhado da resposta recebida (removido)

      // Se o content_type é cadastro_pendente, mostra a tela de animal não encontrado
      if (parsed?.content_type === "cadastro_pendente") {
        updateContentRef.current("animalNotFound", parsed);
        return;
      }

      // Verificação específica para nascimento_animal (erro no registro de nascimento)
      if (parsed?.title === "nascimento_animal") {
        updateContentRef.current("birthError", parsed);
        return;
      }

      // Se data é null e há resumo, mostra tela generalista com informações
      if (parsed?.data === null && parsed?.resumo) {
        updateContentRef.current("genericInfo", {
          genericInfoData: {
            data: parsed.data,
            resumo: extractResumoText(parsed) || parsed.resumo,
            url: parsed.url,
          },
        });
        return;
      }

      // Se tem content_type, usa ele para determinar qual tela mostrar
      // O content_type pode estar em parsed.content_type OU parsed.data.content_type
      // Também verifica o campo title para casos especiais como nascimento_animal
      const contentType =
        parsed.content_type || parsed.data?.content_type || parsed.title;

      // Debug nascimento_animal removido

      if (contentType) {
        switch (contentType) {
          case "animal_detail":
          case "animal-detail":
            // Detecta caso de "não encontrado" e abre tela de NotFound
            {
              const resumoText = (extractResumoText(parsed) || parsed.resumo || "").toLowerCase();
              const isNotFound = (typeof parsed.url === "string" && parsed.url.includes("/animal/not_found")) ||
                                 resumoText.includes("não foi encontrado") ||
                                 resumoText.includes("não encontrado");

              if (isNotFound) {
                updateContentRef.current("notFound", {
                  data: null,
                  resumo: extractResumoText(parsed) || parsed.resumo || "",
                  url: parsed.url || "",
                  mensagem: extractResumoText(parsed) || parsed.resumo || "",
                  opcoes: parsed.opcoes,
                });
                break;
              }
            }

            // Para consulta de animal individual, mostra o passaporte
            // Os dados podem estar em parsed.data ou diretamente em parsed
            {
              const animalData = mapApiAnimalToPassportData(
                parsed.data || parsed
              );
              updateContentRef.current("animalPassport", { animalData });
            }
            break;
          case "animal-updated":
            {
              const animalData = mapApiAnimalToPassportData(parsed.data || parsed);
              updateContentRef.current("genericInfo", {
                genericInfoData: {
                  resumo: extractResumoText(parsed) || parsed.resumo,
                  data: animalData,
                  url: parsed.url,
                },
              });
            }
            break;
          case "exam-list":
          case "exam_detail":
            // Para consulta de exame laboratorial, mostra os detalhes do exame
            // Os dados podem estar em parsed.data ou diretamente em parsed
            const examData = mapApiExamToDetailData(parsed);
            updateContentRef.current("exam-list", { examData });
            break;
          case "exam-detail":
            // Para lista de exames de um animal específico, mostra a nova tela
            // Os dados podem estar em parsed.data ou diretamente em parsed
            const animalExamData = mapApiExamToDetailData(parsed);
            updateContentRef.current("exam-detail", { examData: animalExamData });
            break;
          case "animal_profile_with_nutrition":
            // Para histórico nutricional do animal, mostra a tela específica
            updateContentRef.current("animalNutritionHistory", {
              animalNutritionData: parsed,
            });
            break;
          case "lote_profile_with_nutrition":
            // Para histórico nutricional do lote, mostra a tela específica
            updateContentRef.current("lotNutritionHistory", {
              lotNutritionData: parsed,
            });
            break;
          case "lote_profile_with_sanity":
            // Para histórico de sanidade do lote, mostra a tela específica
            updateContentRef.current("lotHealthHistory", {
              lotHealthHistoryData: parsed,
            });
            break;
          case "animal_profile_with_sanity":
            // Para histórico de sanidade do animal, mostra a tela específica
            updateContentRef.current("animalHealthHistory", {
              animalHealthHistoryData: parsed,
            });
            break;
          case "animal_list":
          case "animal-list":
            const animalListData = {
              data: parsed.data,
              message: parsed.message,
              count: parsed.count || parsed.data?.length || 0,
            };
            updateContentRef.current("animal-list", { animalListData });
            break;
          case "pesagem":
            // Para registro de pesagem, mostra o feedback de pesagem
            updateContentRef.current("weighingFeedback", parsed);
            break;
          case "desmama":
            // Para registro de desmama, mostra o feedback de desmama
            updateContentRef.current("weaningFeedback", parsed);
            break;
          case "nascimento":
            // Para registro de nascimento, mostra o feedback de nascimento
            updateContentRef.current("nascimento", parsed);
            break;
          case "nascimento_animal":
            // Para erro no registro de nascimento, mostra o feedback de erro
            updateContentRef.current("birthError", parsed);
            break;
          case "obito":
            // Para confirmação de óbito animal, mostra o feedback de óbito
            updateContentRef.current("deathFeedback", parsed);
            break;
          case "cio":
            // Para registro de cio, mostra o feedback de cio
            updateContentRef.current("heatRegistration", parsed);
            break;
          case "secagem":
            // Para registro de secagem, mostra o feedback de secagem
            updateContentRef.current("dryingRegistration", parsed);
            break;
          default:
            // Para outros tipos, não abre painel específico
            break;
        }
      } else if (parsed.url === "/animal/register" && parsed.decisao) {
        if (!isMultiTaskRunning) {
          updateContentRef.current("biometricDecision", parsed);
        }
      } else if (
        // Feedback de biometria deve ser acionado apenas quando explicitamente indicado
        parsed.content_type === "biometria_feedback" ||
        (typeof parsed.url === "string" && parsed.url.includes("/biometria"))
      ) {
        const biometricFeedbackData = {
          resumo: parsed.resumo,
          animalName: parsed?.data?.name || parsed?.animalName,
        };
        updateContentRef.current("biometricFeedback", biometricFeedbackData);
      } else if (
        // Caso específico: usuário escolheu não cadastrar biometria agora
        // O backend retorna apenas um resumo com essa mensagem
        typeof parsed?.resumo === "string" &&
        parsed.resumo.includes("cadastro do animal está concluído") &&
        parsed.resumo.toLowerCase().includes("biometria")
      ) {
        const biometricFeedbackData = {
          resumo: parsed.resumo,
          animalName: parsed?.data?.name || parsed?.animalName,
        };
        updateContentRef.current("biometricFeedback", biometricFeedbackData);
      } else if (
        parsed.url === "/nutricao/detalhes" &&
        parsed.data &&
        (parsed.data.handling_type_code === "fornecimento_nutrientes" ||
          parsed.data.entity_type === "animal")
      ) {
        // Se a URL indica nutrição e tem dados de manejo nutricional, abre tela de nutrição
        updateContentRef.current("nutrition", {
          nutritionData: parsed,
        });
      } else if (parsed.url === "/Lotes/lista") {
        // Se a URL indica lista de lotes, abre tela de lista de lotes
        const lotListData = {
          data: parsed.data || [],
          resumo: parsed.resumo,
          url: parsed.url,
        };
        updateContentRef.current("lotList", { lotListData });
      } else if (parsed.url === "/pastos/lista") {
        // Se a URL indica lista de pastos, abre tela de lista de pastos
        const pastureListData = {
          data: Array.isArray(parsed.data) ? parsed.data : (parsed?.data?.data || []),
          resumo: parsed.resumo,
          url: parsed.url,
        };
        updateContentRef.current("pastureList", { pastureListData });
      } else if (
        parsed.url === "/Lotes/detalhes" ||
        (parsed.data &&
          parsed.data.cattle_tags &&
          Array.isArray(parsed.data.cattle_tags))
      ) {
        // Se a URL indica lote ou tem dados de lote, abre tela de detalhes do lote
        updateContentRef.current("lotDetails", parsed);
      } else if (
        parsed.url === "/lotes/detalhes" &&
        parsed.resumo &&
        parsed.resumo.includes("criado com sucesso")
      ) {
        // Se a URL indica criação de lote e tem mensagem de sucesso, abre tela de sucesso
        updateContentRef.current("lotCreated", parsed);
      } else if (
        parsed.url === "/lotes/detalhes" &&
        parsed.resumo &&
        (parsed.resumo.includes("animal adicionado ao lote") ||
          parsed.resumo.includes("animal adicionados ao lote") ||
          parsed.resumo.includes("animais adicionados ao lote"))
      ) {
        // Caso de sucesso na adição de animal ao lote
        updateContentRef.current("animalAddedToLot", parsed);
      } else if (
        parsed.url === "/lotes/detalhes" &&
        parsed.resumo &&
        parsed.resumo.includes("Animais não encontrados")
      ) {
        // Caso de erro (animal não encontrado)
        updateContentRef.current("animalAddError", parsed);
      } else if (
        parsed.url === "/lotes/detalhes" &&
        parsed.resumo &&
        parsed.resumo.includes("animais removidos do lote")
      ) {
        // Se a URL indica remoção de animal do lote, abre tela de sucesso
        updateContentRef.current("animalRemovedFromLot", parsed);
      } else if (
        parsed.url === "/lotes/detalhes" &&
        ((typeof parsed.message === "string" && /desabilitado|desativado/i.test(parsed.message)) ||
         parsed?.data?.is_active === false)
      ) {
        // Feedback de lote desabilitado: abre tela generalista para mensagem clara
        updateContentRef.current("genericInfo", {
          genericInfoData: {
            resumo: typeof parsed.message === "string" ? parsed.message : parsed.resumo || JSON.stringify(parsed),
          },
        });
      } else if (
        parsed.data &&
        (parsed.data._id || parsed.data.cattle_id || parsed.data.name) &&
        parsed.url !== "/nutricao/busca_lote" &&
        parsed.url !== "/nutricao/busca_animal"
      ) {
        // Se não tem content_type mas tem dados de animal individual, assume que é animal_detail
        const animalData = mapApiAnimalToPassportData(parsed.data);
        updateContentRef.current("animalPassport", { animalData });
      } else if (parsed.url === "/nutricao/detalhes") {
        // Se a URL indica nutrição, abre tela de nutrição
        updateContentRef.current("nutrition", { nutritionData: parsed });
      } else if (
        parsed.url === "/reproducao/manejos" &&
        (parsed.data?.operation === "cio" || parsed.resumo?.toLowerCase().includes("cio"))
      ) {
        updateContentRef.current("heatRegistration", parsed);
      } else if (
        parsed.url === "/reproducao/manejos" &&
        (parsed.data?.operation === "secagem" || parsed.resumo?.toLowerCase().includes("secagem"))
      ) {
        updateContentRef.current("dryingRegistration", parsed);
      } else if (
        parsed.url === "/permissoes/usuarios" ||
        parsed.url === "/permissoes"
      ) {
        // Se a URL indica permissões, abre tela de feedback de permissões
        if (parsed.data) {
          // Caso de sucesso - detecta automaticamente se é adição ou remoção
          const operationType = parsed.data.removed_manager_types
            ? "remove"
            : "add";
          updateContentRef.current("permissions", {
            permissionsData: parsed,
            operationType,
          });
        } else {
          // Caso de erro (usuário não encontrado, etc.)
          updateContentRef.current("permissions", {
            permissionsData: parsed,
            operationType: "error",
          });
        }
      } else if (
        parsed.url === "/sanidade/cuidados/detalhes" ||
        parsed?.data?.handling_type_code === "registrar_cuidados"
      ) {
        // Detalhes de cuidados gerais de sanidade (atendimento)
        const healthDetailsData = {
          data: parsed.data,
          resumo: parsed.resumo,
          url: parsed.url,
        };
        updateContentRef.current("healthDetails", { healthDetailsData });
      } else if (parsed.url === "/sanidade/vacinacao/detalhes") {
        // Se a URL indica detalhes de vacinação, abre tela de sanidade
        const healthDetailsData = {
          data: parsed.data,
          resumo: parsed.resumo,
          url: parsed.url,
        };
        updateContentRef.current("healthDetails", { healthDetailsData });
      } else if (
        parsed.url === "/ordenha/producao/detalhes" ||
        parsed?.data?.operation_details?.handling_type_code ===
          "registrar_producao"
      ) {
        // Se a URL indica produção de ordenha, abre tela de detalhes da produção
        updateContentRef.current("productionDetails", parsed);
      } else if (parsed.url === "/ordenha/busca_lote") {
        // Se a URL indica consulta de histórico de ordenha do lote, abre tela específica
        updateContentRef.current("lotMilkingHistory", {
          lotMilkingData: parsed,
        });
      } else if (parsed.url === "/ordenha/busca_animal") {
        // Se a URL indica consulta de histórico de ordenha do animal, abre tela específica
        updateContentRef.current("animalMilkingHistory", {
          animalMilkingData: parsed,
        });
      } else if (parsed.url === "/ordenha/consultas/lista") {
        // Lista geral de histórico de ordenha (animais e/ou lotes)
        updateContentRef.current("milkingHistoryList", {
          milkingHistoryListData: parsed,
        });
      } else if (
        parsed.url === "/manejo/limpeza-tetos/detalhes" ||
        parsed?.data?.operation_details?.handling_type_code ===
          "registrar_manejo"
      ) {
        // Se a URL indica limpeza de tetos, abre tela de detalhes do manejo
        updateContentRef.current("teatCleaningDetails", parsed);
      } else if (
        parsed.url === "/sanidade/endoparasita/detalhes" ||
        parsed?.data?.handling_type_code === "registrar_endoparasita" ||
        parsed.url === "/sanidade/ectoparasita/detalhes" ||
        parsed?.data?.handling_type_code === "registrar_ectoparasita"
      ) {
        // Se a URL indica tratamento de endoparasitas ou ectoparasitas, abre tela de detalhes de sanidade
        const healthDetailsData = {
          data: parsed.data,
          resumo: parsed.resumo,
          url: parsed.url,
        };
        updateContentRef.current("healthDetails", { healthDetailsData });
      } else if (
        parsed.url?.includes("/historico/animal/") ||
        parsed?.data?.operation_details?.historico
      ) {
        // Se a URL indica histórico do animal, abre tela de histórico
        updateContentRef.current("animalHistory", parsed);
      } else if (
        parsed.url?.includes("/reproducao/detalhes") ||
        (parsed?.data?.entity_type === "animal" &&
          parsed?.data?.handling_record_confirmation)
      ) {
        // Se é uma resposta de reprodução, abre tela de detalhes de reprodução
        updateContentRef.current("reproductionDetails", parsed);
      } else if (
        typeof parsed.url === "string" && parsed.url.includes("/reproducao/inseminacao_artificial")
      ) {
        // Inseminação artificial: usa tela de detalhes de reprodução
        updateContentRef.current("reproductionDetails", parsed);
      } else if (
        // Monta natural: abre feedback específico quando identificado por URL ou resumo
        (typeof parsed.url === "string" && parsed.url.includes("/reproducao/monta_natural")) ||
        (typeof parsed.resumo === "string" && parsed.resumo.toLowerCase().includes("monta natural registrada"))
      ) {
        updateContentRef.current("naturalMountFeedback", parsed);
      } else if (
        parsed.url?.includes("/reproducao/home") &&
        parsed.data === null
      ) {
        // Se é uma resposta de reprodução pendente (data null e url /reproducao/home), abre tela de operação pendente
        updateContentRef.current("pendingOperation", parsed);
      } else if (
        (parsed.data === null && parsed?.resumo?.includes("não encontrado")) ||
        (parsed?.data?.api_status === "error" &&
          (parsed?.resumo?.includes("não foi encontrado") ||
            parsed?.resumo?.includes("não encontrado")))
      ) {
        // Se é uma resposta de 'não encontrado' ou erro, abre tela de not found
        updateContentRef.current("notFound", parsed);
      } else if (parsed.url === "/nutricao/busca") {
        // Se a URL indica busca nutricional geral, verifica se há dados
        if (
          parsed.data &&
          Array.isArray(parsed.data) &&
          parsed.data.length > 0
        ) {
          // Detecta histórico por lote quando houver identificação de lote nos registros
          const isLotHistory = parsed.data.some(
            (record: any) =>
              !!record?.operation_lot_id ||
              !!record?.lot_id ||
              !!record?.nome_do_lote
          );
          const firstRecord = parsed.data[0];

          if (isLotHistory) {
            // Histórico agrupado por lote: normaliza estrutura esperada pelo componente
            const normalized = normalizeLotNutritionRecords(parsed.data);
            const lotNutritionData = {
              data: normalized,
              resumo: parsed.resumo,
              url: parsed.url,
            };
            updateContentRef.current("lotNutritionHistory", {
              lotNutritionData,
            });
          } else if (firstRecord && firstRecord.entity_type === "animal") {
            // Extrai informações do animal do primeiro registro
            const animalInfo = {
              entity_type: firstRecord.entity_type,
              _id: firstRecord._id || "",
              _key: firstRecord._key || "",
              _rev: firstRecord._rev || "",
              farm_id: firstRecord.farm_id || "",
              cattle_id: {
                tag: firstRecord.cattle_tag || "",
                country_code: "",
                manufacturer_code: 0,
                unique_id: "",
              },
              name:
                firstRecord.cattle_tag || firstRecord.animal_name || "Animal",
              breed_code:
                firstRecord.breed_code ||
                firstRecord.animal_breed ||
                "Não informado",
              gender:
                firstRecord.gender ||
                firstRecord.animal_gender ||
                "Não informado",
              birth_date: firstRecord.birth_date || null,
              birth_weight_kg: firstRecord.birth_weight_kg || null,
              birth_farm_id: firstRecord.birth_farm_id || null,
              current_farm_id: firstRecord.farm_id || "",
              current_paddock_id: firstRecord.current_paddock_id || null,
              production_purpose: firstRecord.production_purpose || null,
              current_status: firstRecord.current_status || "ativo",
              death_date: firstRecord.death_date || null,
              death_reason: firstRecord.death_reason || null,
              created_at: firstRecord.created_at || "",
              updated_at: firstRecord.updated_at || "",
              historico_nutricao: parsed.data,
            };

            const animalNutritionData = {
              data: animalInfo,
              resumo: parsed.resumo,
              url: parsed.url,
            };
            updateContentRef.current("animalNutritionHistory", {
              animalNutritionData,
            });
          } else {
            // Fallback para histórico por lote
            const lotNutritionData = {
              data: { lancamentos_do_lote: parsed.data },
              resumo: parsed.resumo,
              url: parsed.url,
            };
            updateContentRef.current("lotNutritionHistory", {
              lotNutritionData,
            });
          }
        } else {
          // Se não há dados, abre tela vazia
          const emptyNutritionData = {
            data: [],
            resumo: parsed.resumo,
            url: parsed.url,
          };
          updateContentRef.current("emptyNutritionHistory", {
            emptyNutritionData,
          });
        }
      } else if (parsed.url === "/nutricao/busca_animal") {
        // Se a URL indica busca nutricional de animal, verifica se há dados
        if (
          parsed.data &&
          (parsed.data.historico_nutricao || parsed.data.length > 0)
        ) {
          const animalNutritionData = {
            data: parsed.data,
            resumo: parsed.resumo,
            url: parsed.url,
          };
          updateContentRef.current("animalNutritionHistory", {
            animalNutritionData,
          });
        } else {
          // Se não há dados, abre tela vazia
          const emptyNutritionData = {
            data: [],
            resumo: parsed.resumo,
            url: parsed.url,
          };
          updateContentRef.current("emptyNutritionHistory", {
            emptyNutritionData,
          });
        }
      } else if (
        parsed.url === "/lote/perfil-nutricao" ||
        parsed.url === "/nutricao/busca_lote"
      ) {
        // Se a URL indica histórico nutricional do lote, verifica se há dados
        if (
          parsed.data &&
          (parsed.data.lancamentos_do_lote || parsed.data.length > 0)
        ) {
          const data = Array.isArray(parsed.data)
            ? normalizeLotNutritionRecords(parsed.data)
            : parsed.data;
          const lotNutritionData = {
            data,
            resumo: parsed.resumo,
            url: parsed.url,
          };
          updateContentRef.current("lotNutritionHistory", { lotNutritionData });
        } else {
          // Se não há dados, abre tela vazia
          const emptyNutritionData = {
            data: [],
            resumo: parsed.resumo,
            url: parsed.url,
          };
          updateContentRef.current("emptyNutritionHistory", {
            emptyNutritionData,
          });
        }
      } else if (
        parsed.url === "/lote/perfil-sanidade" ||
        (parsed.url === "/sanidade/consultas" && parsed.data?.nome_do_lote)
      ) {
        // Se a URL indica histórico de sanidade do lote, abre tela específica
        const lotHealthHistoryData = {
          data: parsed.data,
          resumo: parsed.resumo,
          url: parsed.url,
        };
        updateContentRef.current("lotHealthHistory", { lotHealthHistoryData });
      } else if (parsed.url === "/animal/perfil-sanidade") {
        // Se a URL indica histórico de sanidade do animal, abre tela específica
        const animalHealthHistoryData = {
          data: parsed.data,
          content_type: parsed.content_type,
          operation_status: parsed.operation_status,
          profile_found: parsed.profile_found,
          resumo: parsed.resumo,
          url: parsed.url,
        };
        updateContentRef.current("animalHealthHistory", {
          animalHealthHistoryData,
        });
      } else if (parsed.url === "/reproducao/lista") {
        // Se a URL indica histórico reprodutivo, abre tela específica
        const reproductiveHistoryData = {
          data: parsed.data,
          resumo: parsed.resumo,
          url: parsed.url,
        };
        updateContentRef.current("reproductiveHistory", { reproductiveHistoryData });
      } else {
        // FALLBACK: Se chegou até aqui, é uma resposta inesperada que não foi processada
        // Isso inclui respostas com operation_code mas sem content_type válido
        console.warn("Resposta inesperada da IA detectada:", parsed);
        // Se houver conteúdo informativo (resumo/URL), mostrar de forma amigável
        if (typeof parsed?.resumo === "string" || typeof parsed?.url === "string") {
          updateContentRef.current("genericInfo", {
            genericInfoData: {
              data: parsed.dados || parsed.data || null,
              resumo: parsed.resumo || "",
              url: parsed.url || "",
            },
          });
        } else {
          updateContentRef.current("errorView", {
            errorData: {
              data: parsed.dados || parsed.data || parsed,
              resumo: parsed.resumo || "Resposta inesperada da IA",
              url: parsed.url || "",
              error: "Resposta inesperada da IA",
              operation_code: parsed.operation_code,
              status: parsed.status,
              rawResponse: JSON.stringify(parsed, null, 2),
            },
          });
        }
      }
    }, [messages, sessionId]);

    // Handlers do menu/flow - Otimizado para resposta mais rápida
     const handleMenuClick = useCallback((event?: React.MouseEvent) => {
       // Prevenir propagação de eventos para evitar conflitos
       if (event) {
         event.preventDefault();
         event.stopPropagation();
       }
       // Se o modo Master estiver ativo, não abrir o seletor de gestores
       if (forceMasterEnabled) {
         return;
       }
       
       // Usar requestAnimationFrame para garantir resposta imediata
       requestAnimationFrame(() => {
         setShowGestores((prev) => !prev);
       });
     }, [forceMasterEnabled]);

     // Handler para eventos de teclado (acessibilidade)
     const handleMenuKeyDown = useCallback((event: React.KeyboardEvent) => {
       if (event.key === 'Enter' || event.key === ' ') {
         event.preventDefault();
         event.stopPropagation();
         if (!forceMasterEnabled) {
           handleMenuClick();
         }
       }
     }, [handleMenuClick, forceMasterEnabled]);

    const handleMenuClose = () => {
      setShowGestores(false);
    };

    const handleFlowSelect = (flowId: string) => {
      // Normaliza o id do gestor laboratorial
      const normalizedId = flowId === "gestor_laboratorial" ? "laboratory" : flowId;

      // Exceção: permitir seleção do gestor laboratorial mesmo com Master fixo
      if (forceMasterEnabled && normalizedId !== "laboratory") {
        setSelectedFlow("master");
        setShowGestores(false);
        handleMenuClose();
        return;
      }

      setSelectedFlow(normalizedId);
      setHasManualFlowSelection(true);
      setShowGestores(false);
      handleMenuClose();
    };

    const handleSendMessage = useCallback(async () => {
      if (!inputValue.trim() || !session) return;

      setIsLoading(true);
      const messageText = inputValue.trim();
      setInputValue("");

      try {
        await sendMessage(messageText, selectedFlow);
      } catch (error) {
        console.error("Erro ao enviar mensagem:", error);
      } finally {
        setIsLoading(false);
      }
    }, [inputValue, session, sendMessage, selectedFlow]);

    const handleKeyPress = useCallback(
      (event: React.KeyboardEvent) => {
        if (event.key === "Enter" && !event.shiftKey) {
          event.preventDefault();
          handleSendMessage();
        }
      },
      [handleSendMessage]
    );

    // If organization is not found, use a fallback organization
    const fallbackOrganization: Organization = {
      id: "muu-general",
      name: "Assistente MUU",
      type: "other",
      description: "Assistente de IA para agropecuária",
    };

    const effectiveOrganization = organization || fallbackOrganization;

    // Coordenadas compactas para cabeçalho
    const coordsText = useMemo(() => {
      const loc: any = (effectiveOrganization as any).location;
      const coords = loc?.coordinates;
      const lat = coords?.latitude;
      const lon = coords?.longitude;
      if (typeof lat !== "number" || typeof lon !== "number") return null;
      const latHem = lat < 0 ? "S" : "N";
      const lonHem = lon < 0 ? "W" : "E";
      const latStr = `${lat.toFixed(4)}° ${latHem}`;
      const lonStr = `${lon.toFixed(4)}° ${lonHem}`;
      return `${latStr}, ${lonStr}`;
    }, [effectiveOrganization]);

    const orgColor = useMemo(
      () =>
        getOrganizationColor(
          "farm" in effectiveOrganization
            ? "other"
            : "type" in effectiveOrganization
            ? effectiveOrganization.type
            : "other"
        ),
      [effectiveOrganization]
    );

    // Add missing currentFlowColors variable
    const currentFlowColors = useMemo(
      () => getFlowColors(selectedFlow),
      [selectedFlow]
    );

    if (!session) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: NEUTRAL_COLORS.gray500,
          }}
        >
          <Typography variant="h6">Selecione uma conversa</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden", // Root prevents outer scroll
          minHeight: 0,
        }}
      >
        {/* Header - Fixed */}
        <Paper
          elevation={1}
          sx={{
            p: SPACING.md, // 16px padding
            borderRadius: BORDER_RADIUS.none,
            borderBottom: `1px solid ${NEUTRAL_COLORS.gray200}`,
            bgcolor: isChatDisabled ? NEUTRAL_COLORS.gray100 : "white",
            flexShrink: 0, // Prevent shrinking
            minHeight: "80px",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <IconButton
              onClick={onBackClick}
              sx={{
                color: NEUTRAL_COLORS.gray600,
                bgcolor: NEUTRAL_COLORS.gray50,
                border: `1px solid ${NEUTRAL_COLORS.gray200}`,
                borderRadius: BORDER_RADIUS.md,
                width: 40,
                height: 40,
                "&:hover": {
                  bgcolor: BRAND_COLORS.primaryLight,
                  borderColor: BRAND_COLORS.primary,
                  color: BRAND_COLORS.primary,
                  transform: "translateY(-1px)",
                  boxShadow: "0 2px 8px rgba(107, 196, 60, 0.15)",
                },
                transition: "all 0.2s ease-in-out",
              }}
            >
              <ArrowBack sx={{ fontSize: 20 }} />
            </IconButton>
            <Avatar
              sx={{
                bgcolor: isChatDisabled ? NEUTRAL_COLORS.gray200 : orgColor,
                ...AVATAR_SIZES.md,
              }}
            >
              {(
                ("type" in effectiveOrganization && (effectiveOrganization as any).type === "farm") ||
                ("farm" in effectiveOrganization)
              ) ? (
                <img
                  src={isChatDisabled ? "/Fazenda.svg" : "/Fazenda-branco.svg"}
                  alt="Fazenda"
                  style={{ width: 28, height: 28 }}
                />
              ) : (
                React.createElement(
                  getOrganizationIcon(
                    "farm" in effectiveOrganization
                      ? "other"
                      : "type" in effectiveOrganization
                      ? (effectiveOrganization as any).type
                      : "other"
                  )
                )
              )}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              {session?.title && (
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: NEUTRAL_COLORS.gray800 }}
                >
                  {session.title.length > 60
                    ? `${session.title.substring(0, 60)}...`
                    : session.title}
                </Typography>
              )}
              <Typography
                variant="body2"
                sx={{ color: NEUTRAL_COLORS.gray600, fontSize: "0.875rem" }}
              >
                {effectiveOrganization.name}
              </Typography>
              {coordsText && (
                <Typography
                  variant="caption"
                  sx={{ color: NEUTRAL_COLORS.gray500, fontSize: "0.75rem" }}
                >
                  {coordsText}
                </Typography>
              )}
            </Box>
          </Box>
          {/* Acesso rápido fixo ao hub de exames */}
          <Box sx={{ mt: 1, display: "flex", alignItems: "center" }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                handleFlowSelect("laboratory");
                updateContent("examsHub");
              }}
              sx={{
                borderRadius: "10px",
                fontSize: "0.78rem",
                fontWeight: 600,
                textTransform: "none",
                bgcolor: "transparent",
                color: NEUTRAL_COLORS.gray700,
                px: 1.25,
                py: 0.5,
                minHeight: 28,
                borderColor: NEUTRAL_COLORS.gray200,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: NEUTRAL_COLORS.gray50,
                  borderColor: NEUTRAL_COLORS.gray300,
                },
              }}
              startIcon={<Science sx={{ fontSize: 15 }} />}
            >
              Exames
            </Button>
            {isChatDisabled && (
              <Typography sx={{ ml: 2, color: NEUTRAL_COLORS.gray600, fontSize: "0.8rem" }}>
                Chat desabilitado nos exames
              </Typography>
            )}
          </Box>
        </Paper>

        {/* Messages Container - Scrollable */}
        <Box
          sx={{
            flex: 1, // Take remaining space
            overflowY: "auto", // Enable vertical scrolling
            p: SPACING.md,
            bgcolor: NEUTRAL_COLORS.gray50,
            minHeight: 0, // Important for flex children
            // Hide scrollbar visually but keep scroll functional (cross-browser)
            msOverflowStyle: "none", // IE/Edge legacy
            scrollbarWidth: "none", // Firefox
            "&::-webkit-scrollbar": {
              width: 0,
              height: 0,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              background: "transparent",
            },
          }}
        >
          {/* Renderizar apenas as últimas 50 mensagens para melhor performance */}
          {sessionMessages.slice(-50).map((message) => (
            <MessageBubble
              key={message.id}
              message={message}
              organization={effectiveOrganization as Organization}
              selectedFlow={selectedFlow}
            />
          ))}
          <div ref={messagesEndRef} />
        </Box>

        {/* Switch “Auto” removido deste ponto para reposicionamento abaixo do chat */}

        {/* Input Section - Fixed */}
        <Paper
          elevation={2}
          sx={{
            borderRadius: 0,
            borderTop: `1px solid ${NEUTRAL_COLORS.gray200}`,
            bgcolor: "white",
            p: 0,
            flexShrink: 0, // Prevent shrinking
            minHeight: "80px",
            boxShadow: "0 -2px 8px rgba(0, 0, 0, 0.1)",
            transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
            transform: showGestores ? "translateY(-280px)" : "translateY(0)",
          }}
        >
          {/* Barra de Input Inline */}
          <Box sx={{ p: 2.5, position: "relative" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                position: "relative", // Garantir contexto de posicionamento
              zIndex: 1, // Garantir que o container esteja posicionado corretamente
            }}
          >
              {/* Botão "+" abre o sheet de gestores (desabilitado no modo Master) */}
              <IconButton
                onClick={handleMenuClick}
                onKeyDown={handleMenuKeyDown}
                disabled={forceMasterEnabled || isChatDisabled}
                disableRipple={false}
                sx={{
                  width: 40, // Aumentado de 32 para 40px para maior área clicável
                  height: 40, // Aumentado de 32 para 40px para maior área clicável
                  borderRadius: "50%",
                  bgcolor: currentFlowColors.primary,
                  color: "white",
                  boxShadow: `0 2px 8px ${currentFlowColors.primary}55`,
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)", // Reduzido de 0.3s para 0.2s para resposta mais rápida
                  transform: showGestores ? "rotate(45deg)" : "rotate(0deg)",
                  cursor: forceMasterEnabled ? "not-allowed" : "pointer",
                  // Garantir que toda a área seja clicável
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative", // Garantir posicionamento correto
                  zIndex: 10, // Garantir que esteja acima de outros elementos
                  // Forçar área clicável em toda a superfície
                  padding: 0,
                  margin: 0,
                  minWidth: 40,
                  minHeight: 40,
                  // Garantir que eventos de pointer funcionem em toda a área
                  pointerEvents: forceMasterEnabled ? "none" : "auto",
                  // Melhor feedback visual
                  "&:hover": {
                    transform: showGestores ? "rotate(45deg) scale(1.08)" : "scale(1.08)", // Ligeiramente maior para melhor feedback
                    bgcolor: currentFlowColors.primary,
                    boxShadow: `0 4px 12px ${currentFlowColors.primary}66`, // Sombra mais pronunciada no hover
                  },
                  // Estado ativo para feedback imediato
                  "&:active": {
                    transform: showGestores ? "rotate(45deg) scale(0.95)" : "scale(0.95)",
                    transition: "all 0.1s cubic-bezier(0.4, 0, 0.2, 1)", // Transição mais rápida para o estado ativo
                  },
                  // Assegurar visual correto quando desabilitado (fixa fundo azul e ícone branco)
                  "&.Mui-disabled": {
                    bgcolor: isChatDisabled ? NEUTRAL_COLORS.gray200 : currentFlowColors.primary,
                    color: isChatDisabled ? NEUTRAL_COLORS.gray500 : 'white',
                    boxShadow: isChatDisabled ? 'none' : `0 2px 8px ${currentFlowColors.primary}55`,
                  },
                  // Garante que o ícone interno permaneça branco
                  "& .MuiSvgIcon-root": {
                    color: "white",
                  },
                  // Remover borda ao focar (sem outline)
                  "&:focus, &:focus-visible, &.Mui-focusVisible": {
                    outline: "none",
                    boxShadow: "none",
                  },
                  // Garantir que o botão seja totalmente interativo
                  "& .MuiTouchRipple-root": {
                    borderRadius: "50%",
                    width: "100%",
                    height: "100%",
                  },
                  // Garantir que o ícone não interfira com os eventos de clique
                  "& > *": {
                    pointerEvents: "none", // Ícone não deve interceptar eventos
                  },
                }}
                aria-label="Abrir gestores"
                aria-haspopup="menu"
                aria-expanded={showGestores}
                // Adicionar propriedades para melhor acessibilidade e responsividade
                tabIndex={0}
                role="button"
              >
                {!showGestores && selectedFlow ? (
                  selectedFlow === "batch" ? (
                    <img
                      src="/gestores/lote-branco.svg"
                      alt="Lote"
                      style={{
                        width: 36,
                        height: 36,
                      }}
                    />
                  ) : (
                    (() => {
                      const icon = getFlowIcon(selectedFlow);
                      if ((icon as any).type === "img") {
                        return React.cloneElement(icon as any, {
                          style: {
                            ...(icon as any).props.style,
                            width: 24,
                            height: 24,
                            filter: "brightness(0) invert(1)",
                          },
                        });
                      }
                      return React.cloneElement(icon as any, {
                        sx: { fontSize: 24, color: "white" },
                      });
                    })()
                  )
                ) : (
                  <Add sx={{ fontSize: 24, color: "white" }} />
                )}
              </IconButton>

              {/* Campo de texto inline */}
              <TextField
                ref={inputRef}
                fullWidth
                multiline
                maxRows={4}
                value={inputValue}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder="Digite sua mensagem aqui..."
                variant="outlined"
                disabled={isLoading || isChatDisabled}
                sx={{
                  flex: 1,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    bgcolor: NEUTRAL_COLORS.gray50,
                    fontSize: "15px",
                    lineHeight: 1.5,
                    padding: "12px 16px",
                    border: `1px solid transparent`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      bgcolor: NEUTRAL_COLORS.gray50,
                      borderColor: currentFlowColors.primary + "30",
                    },
                    "&.Mui-focused": {
                      bgcolor: "white",
                      borderColor: currentFlowColors.primary,
                      boxShadow: `0 0 0 3px ${currentFlowColors.primary}15`,
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  },
                  "& .MuiInputBase-input": {
                    "&::placeholder": {
                      color: NEUTRAL_COLORS.gray400,
                      opacity: 1,
                    },
                  },
                }}
              />

              {/* Botão de enviar */}
              <IconButton
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading || isChatDisabled}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: currentFlowColors.primary,
                  color: "white",
                  borderRadius: "10px",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    bgcolor: currentFlowColors.primary,
                    transform: "translateY(-1px) scale(1.05)",
                    boxShadow: `0 6px 16px ${currentFlowColors.primary}40`,
                  },
                  "&:active": {
                    transform: "translateY(0px) scale(0.98)",
                  },
                  "&:disabled": {
                    bgcolor: NEUTRAL_COLORS.gray200,
                    color: NEUTRAL_COLORS.gray400,
                    transform: "none",
                    boxShadow: "none",
                  },
                }}
              >
                {isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <Send sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Box>

            
          </Box>
        </Paper>

        {/* Switch “Auto” removido temporariamente */}

        {/* Sheet de Gestores - Sobe de baixo */}
        <SwipeableDrawer
          anchor="bottom"
          open={showGestores}
          onClose={handleMenuClose}
          onOpen={() => setShowGestores(true)}
          disableSwipeToOpen={true}
          sx={{
            // Desativa totalmente a área de swipe no web
            "& .MuiSwipeArea-root": {
              pointerEvents: "none",
              height: 0,
              width: 0,
            },
            "& .MuiDrawer-paper": {
              borderRadius: "20px 20px 0 0",
              maxHeight: "70vh",
              minHeight: "280px",
              bgcolor: "white", // Fundo branco sólido
              boxShadow: "0 -8px 32px rgba(0, 0, 0, 0.12)",
              // Ajustar largura para ocupar apenas a área do chat
              width: { 
                xs: "100%", // Mobile: largura total
                md: "350px", // Desktop: largura fixa do painel do chat
                lg: "400px"  // Desktop large: largura fixa do painel do chat
              },
              left: { 
                xs: 0, // Mobile: alinhado à esquerda da tela
                md: 0, // Desktop: alinhado à esquerda do painel do chat
                lg: 0  // Desktop large: alinhado à esquerda do painel do chat
              },
              right: "auto", // Remove alinhamento à direita
              display: "flex",
              flexDirection: "column",
            },
          }}
          ModalProps={{
            keepMounted: true,
            // Remove o backdrop (fundo escuro)
            hideBackdrop: true,
            // Remove a sobreposição
            disablePortal: false,
          }}
        >


          {/* Header do Sheet */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: (theme) => (theme.zIndex.appBar || 1100) + 1,
              bgcolor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 3,
              py: 2.5, // Aumentado o padding vertical após remoção do handle
              borderBottom: `1px solid ${NEUTRAL_COLORS.gray200}`,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: NEUTRAL_COLORS.gray800,
                fontSize: "1.1rem",
              }}
            >
              Selecionar Gestor
            </Typography>
            <IconButton
              onClick={handleMenuClose}
              sx={{
                width: 32,
                height: 32,
                bgcolor: NEUTRAL_COLORS.gray100,
                "&:hover": {
                  bgcolor: NEUTRAL_COLORS.gray200,
                },
              }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>

          {/* Ações rápidas fixas (fora do container rolável) */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: (theme) => theme.zIndex.appBar || 1100,
              bgcolor: "#fff",
              borderBottom: `1px solid ${NEUTRAL_COLORS.gray200}`,
              px: 3,
              py: 1.25,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                handleFlowSelect("laboratory");
                updateContent("examsHub");
              }}
              sx={{
                borderRadius: "10px",
                fontSize: "0.78rem",
                fontWeight: 600,
                textTransform: "none",
                bgcolor: "transparent",
                color: NEUTRAL_COLORS.gray700,
                px: 1.25,
                py: 0.5,
                minHeight: 28,
                borderColor: NEUTRAL_COLORS.gray200,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: NEUTRAL_COLORS.gray50,
                  borderColor: NEUTRAL_COLORS.gray300,
                },
              }}
              startIcon={<Science sx={{ fontSize: 15 }} />}
            >
              Exames
            </Button>
          </Box>

          {/* Conteúdo do Sheet */}
          <Box 
            sx={{ 
              px: 3, 
              py: 2, 
              bgcolor: '#fff',
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              // Ocultar visualmente a barra de rolagem (cross-browser) mantendo o scroll
              msOverflowStyle: "none", // IE/Edge legacy
              scrollbarWidth: "none", // Firefox
              "&::-webkit-scrollbar": {
                width: 0,
                height: 0,
              },
              "&::-webkit-scrollbar-track": {
                background: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "transparent",
              },
            }}
          >

            {/* Lista vertical de gestores */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {availableFlows.map((flow) => {
                const isSelected = selectedFlow === flow.id;
                const flowColors = getFlowColors(flow.id);
                const isMaster = flow.id === "master";

                return (
                  <Box
                    key={flow.id}
                    onClick={() => {
                      // Desabilitar seleção de gestores quando modo Master estiver ativo
                      if (forceMasterEnabled && flow.id !== 'master') return;
                      handleFlowSelect(flow.id);
                    }}
                    sx={{
                      py: 1.5,
                      px: 2,
                      borderRadius: "12px",
                      minHeight: 64,
                      mx: 0,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      cursor: forceMasterEnabled && flow.id !== 'master' ? "not-allowed" : "pointer",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        bgcolor: forceMasterEnabled && flow.id !== 'master' ? 'transparent' : flowColors.hover,
                        transform: forceMasterEnabled && flow.id !== 'master' ? 'none' : "translateX(4px)",
                      },
                      ...(isSelected && {
                        bgcolor: flowColors.light,
                        borderLeft: `4px solid ${flowColors.primary}`,
                        "&:hover": {
                          bgcolor: flowColors.hover,
                        },
                      }),
                      ...(forceMasterEnabled && flow.id !== 'master' ? {
                        opacity: 0.5,
                        pointerEvents: 'none',
                      } : {}),
                    }}
                  >
                    {/* Ícone do gestor */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 44,
                        height: 44,
                        borderRadius: "12px",
                        bgcolor: flowColors.light,
                        border: `2px solid ${flowColors.primary}20`,
                      }}
                    >
                      {getFlowIcon(flow.id)}
                    </Box>

                    {/* Detalhes do gestor */}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: NEUTRAL_COLORS.gray800,
                          fontSize: "0.9rem",
                        }}
                      >
                        {flow.name?.replace(" Flow", "") || "Master"}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: NEUTRAL_COLORS.gray500,
                          fontSize: "0.75rem",
                        }}
                      >
                        {isMaster ? "Modo padrão" : "Especializado"}
                      </Typography>
                    </Box>

                    {/* Indicador de seleção */}
                    {isSelected && (
                      <Box
                        sx={{
                          width: 24,
                          height: 24,
                          borderRadius: "50%",
                          bgcolor: flowColors.primary,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <CheckCircle sx={{ fontSize: 16, color: "white" }} />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        </SwipeableDrawer>

        <Dialog
          fullScreen
          open={openMobileExams}
          onClose={handleCloseMobileExams}
          TransitionComponent={MobileExamsTransition}
        >
          <AppBar position="sticky" elevation={0} sx={{ bgcolor: "white", color: NEUTRAL_COLORS.gray800 }}>
            <Toolbar sx={{ px: 1, gap: 1, minHeight: { xs: 56, sm: 64 } }}>
              <IconButton edge="start" color="inherit" onClick={handleCloseMobileExams}>
                <Close />
              </IconButton>
              <Science sx={{ fontSize: 20, color: DESIGN_TOKENS.colors.brand.primary }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1 }}>
                Exames
              </Typography>
            </Toolbar>
          </AppBar>
          <Box sx={{ flex: 1, overflow: "auto", bgcolor: "background.paper" }}>
            <MobileExamsFlow />
          </Box>
        </Dialog>
      </Box>
    );
  }
);
